import {
  infiniteQueryOptions,
  keepPreviousData,
  QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import axios, { AxiosError, AxiosRequestConfig, isAxiosError } from "axios";
import { kebabCase } from "change-case";
import { TLEditorSnapshot } from "tldraw";
import Cookies from "universal-cookie";

import { ColorVariant } from "@/components/canvas-editor/components/Container";
import { RelationshipType } from "@/components/canvas-editor/shapes/shapes";
import {
  type LabelerDetail,
  labelerPollInterval,
  type LabelerSummary,
} from "@/lib/labeler-logic";
import { assertSaveAccepted } from "@/lib/save-refusal";
import {
  Feed,
  FeedPage,
  PostHydrationResponse,
  UserHydrationResponse,
  Manifest,
  User,
  FeedHistory,
  StickyPost,
  ExternalFeed,
  SortingOption,
  SortingParams,
  TopContentResponse,
  StarterPackRecord,
  ListRecord,
  Campaign,
  InboundCampaign,
  PublicFeed,
  CampaignState,
  FeedParameter,
  DependencyIntent,
  FilterTypes,
  Post,
  Tag,
  SavedTag,
  type ProgrammaticAssetsResponse,
  CocoreStatus,
  CocorePairing,
  CocorePairPoll,
  FeedPulse,
  FeedPulseNowResult,
  FeedPulseStatus,
  FeedRateLimits,
  QuotaIncreaseRequest,
  QuotaGrant,
  RecentRateLimits,
} from "@/lib/types";
import { urlToAtUri } from "@/lib/utils";

const requestSettings: AxiosRequestConfig = {
  timeout: 90_000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
};

const requestSetingsFormData: AxiosRequestConfig = {
  ...requestSettings,
  headers: {
    "Content-Type": "multipart/form-data",
  },
};

const cookies = new Cookies(null, { path: "/" });
const hostname =
  process.env.NODE_ENV === "production" ? "api.graze.social" : "localhost:4321";
const baseUrl =
  process.env.NODE_ENV === "production"
    ? `https://${hostname}`
    : `http://${hostname}`;
const host =
  process.env.NODE_ENV === "production" ? `${baseUrl}/app` : `${baseUrl}/api`;

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: { queries: { staleTime: 10000 } },
  });
}

/** The browser's client. Server renders get their own from getRouter. */
export const queryClient = createQueryClient();

export function getUserId() {
  if (!cookies.get(CookiesKeys.userId)) {
    const err = new AxiosError("No user id");
    err.status = 401;
    throw err;
  }

  return cookies.get(CookiesKeys.userId) as string;
}

function setUserIdCookie(userId: number) {
  cookies.set(CookiesKeys.userId, userId, {
    path: "/",
    secure: true,
    sameSite: "none",
    expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
  });
}

interface OauthStatusResponse {
  authenticated?: boolean;
  is_oauth?: boolean;
  user?: {
    id: number;
    did: string;
    handle: string;
  };
}

async function checkOauthStatus() {
  return await axios.get<OauthStatusResponse>(
    `${host}/api/v1/oidc/user/me`,
    requestSettings,
  );
}

export async function checkAuth() {
  if (process.env.NODE_ENV === "production") {
    let res: OauthStatusResponse | null = null;

    try {
      res = (await checkOauthStatus()).data;
    } catch (e) {
      if (
        isAxiosError(e) &&
        e.response?.status === 401 &&
        e.response.data &&
        typeof e.response.data === "object" &&
        "detail" in e.response.data &&
        (e.response.data as { detail?: string }).detail === "Invalid session"
      ) {
        throw redirect({ to: "/login" });
      } else {
        throw e;
      }
    }

    // User is API only, make them oauth again
    if (!res.authenticated && res.is_oauth) {
      throw redirect({ to: "/login" });
    }

    // The userId cookie is written from JS, so Safari's ITP caps it at 7 days
    // whatever its expiry says, while the API's httpOnly session cookie lives
    // on. Without this, a Safari user 7+ days past login gets "No user id" on
    // every call that sends user_id, and /login (which sees a live session)
    // bounces them straight back to /app in a loop. The server knows who they
    // are, so restore the cookie from it on every auth check.
    if (res.authenticated && res.user?.id) {
      setUserIdCookie(res.user.id);
    }

    return;
  }

  // legacy
  return getUserId();
}

/** `purpose` widens the OAuth scope for THIS sign-in only. "cocore" adds the
 *  grant that lets Graze proxy an XRPC call to co/core on the user's behalf;
 *  people who never connect co/core are never asked for it. */
export async function getLoginUrl(purpose?: "cocore") {
  const response = await axios.get<{ login_url: string }>(
    `${host}/api/v1/oidc/login${purpose ? `?purpose=${purpose}` : ""}`,
    requestSettings,
  );

  return response.data.login_url;
}

export async function loginOAuth(code: string) {
  const creds = await axios.get<
    | {
        user: { id: number };
      }
    | {
        linked: boolean;
      }
  >(`${host}/api/v1/oidc/auth/callback?code=${code}`, requestSettings);

  if ("user" in creds.data) {
    setUserIdCookie(creds.data.user.id);
  } else if ("linked" in creds.data) {
    return { linked: true };
  }
}

export async function newsroomLoginCallback(options: {
  aip_auth_token: string;
  email_address: string;
}): Promise<void> {
  const creds = await axios.post<{
    session_string: string;
    user: { id: number };
  }>(`${host}/newsrooms/callback`, options, requestSettings);

  setUserIdCookie(creds.data.user.id);
}

export async function logout() {
  cookies.remove(CookiesKeys.sessionString, { path: "/" });
  cookies.remove(CookiesKeys.authCookie, { path: "/" });
  cookies.remove(CookiesKeys.authToken, { path: "/" });
  cookies.remove(CookiesKeys.userId, { path: "/" });

  queryClient.removeQueries();

  await axios.post(`${host}/logout`, {}, requestSettings);
}

export interface GetMyFeedsOptions {
  page?: number;
  /** Feeds per page. Defaults to 10 server-side; the API caps it at 100. */
  page_size?: number;
  search_term?: string;
  sort_by?:
    | "name"
    | "created_at"
    | "page_renders"
    | "unique_users"
    | "post_renders"
    | "monetized";
  order?: "asc" | "desc";
  tags?: number[];
  /**
   * Whose feeds to list. "owned" (the server's default, and what every caller but the
   * dashboard wants), "shared" for feeds others have made you a collaborator on, or "all"
   * for one list containing both.
   */
  access?: "owned" | "shared" | "all";
}

export interface PaymentHistoryOptions {
  cursor?: string;
  limit: number;
}

export interface PaymentHistoryItem {
  amount: number;
  currency: string;
  created: string;
  description: string | null;
  id: string;
  metadata: {
    credit_account_id: string;
    debt_account_id: string;
    total_payout: string;
    transactions: string;
  };
  paid_user: { username: string };
  status: "succeeded";
  algorithms: {
    id: number;
    name: string;
  }[];
  campaigns: {
    id: number;
    name: string;
  }[];
}

export interface PayoutHistoryItem {
  amount: number;
  currency: string;
  created: string;
  description: string | null;
  id: string;
  metadata: {
    credit_account_id: string;
    debt_account_id: string;
    total_payout: string;
    transactions: string;
  };
  paying_user: { username: string };
  status: "succeeded";
  algorithms: {
    id: number;
    name: string;
  }[];
  campaigns: {
    id: number;
    name: string;
  }[];
}

interface PaymentHistoryResponse {
  data: Array<PaymentHistoryItem>;
  next_cursor: string | null;
  previous_cursor: string | null;
}

interface PayoutHistoryResponse {
  data: Array<PayoutHistoryItem>;
  next_cursor: string | null;
  previous_cursor: string | null;
}

async function getPayoutsHistory({
  cursor,
  limit = 10,
}: PaymentHistoryOptions): Promise<PayoutHistoryResponse> {
  const params = new URLSearchParams();

  if (cursor) {
    params.append("cursor", cursor);
  }

  params.append("limit", limit.toString());
  params.append("user_id", getUserId());

  return await axios
    .get<PayoutHistoryResponse>(
      `${host}/api/v1/payments/payout-history?${params.toString()}`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const payoutsHistoryQueryOptions = (options: PaymentHistoryOptions) =>
  queryOptions({
    queryKey: ["payouts-history", options.cursor],
    queryFn: () => getPayoutsHistory(options),
    placeholderData: keepPreviousData,
  });

async function getPaymentHistory({
  cursor,
  limit = 10,
}: PaymentHistoryOptions): Promise<PaymentHistoryResponse> {
  const params = new URLSearchParams();

  if (cursor) {
    params.append("cursor", cursor);
  }

  params.append("limit", limit.toString());
  params.append("user_id", getUserId());

  return await axios
    .get<PaymentHistoryResponse>(
      `${host}/api/v1/payments/payment-history?${params.toString()}`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const paymentHistoryQueryOptions = (options: PaymentHistoryOptions) =>
  queryOptions({
    queryKey: ["payment-history", options.cursor],
    queryFn: () => getPaymentHistory(options),
    placeholderData: keepPreviousData,
  });

async function getMyFeeds({
  page = 1,
  page_size,
  sort_by,
  order,
  search_term,
  tags,
  access,
}: GetMyFeedsOptions) {
  const params = new URLSearchParams();

  params.append("sort_by", sort_by || "updated_at");
  params.append("order", order || "desc");
  params.append("page", page.toString());
  params.append("user_id", getUserId());
  params.append("search_term", search_term || "");

  if (access) {
    params.append("access", access);
  }

  if (page_size) {
    params.append("page_size", page_size.toString());
  }

  if (tags) {
    tags.forEach((tag) => {
      params.append("tags", tag.toString());
    });
  }

  return await axios
    .get<FeedPage>(`${host}/my_feeds?${params.toString()}`, requestSettings)
    .then((res) => res.data);
}

export const myFeedsQueryOptions = (options: GetMyFeedsOptions) =>
  queryOptions({
    queryKey: [
      "my-feeds",
      options.order,
      options.sort_by,
      options.page,
      options.page_size,
      options.search_term,
      options.tags,
      options.access,
    ],
    queryFn: () => getMyFeeds(options),
    placeholderData: keepPreviousData,
  });

export const myFeedsInfiniteQueryOptions = (options: GetMyFeedsOptions) =>
  infiniteQueryOptions({
    queryKey: [
      "my-feeds-infinite",
      options.order,
      options.sort_by,
      options.page,
      options.page_size,
      options.search_term,
      options.tags,
      options.access,
    ],
    queryFn: ({ pageParam }) =>
      getMyFeeds({ ...options, page: pageParam }).then((res) => ({
        data: res.user_algos,
        ...res,
      })),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;

      if (nextPage > lastPage.total_pages) {
        return undefined;
      }

      return nextPage;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
  });

async function getFeedHistory(feedId: number) {
  return await axios
    .get<FeedHistory>(
      `${host}/my_feeds/${feedId}/with_history`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const feedHistoryQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["my-feeds", feedId, "history"],
    queryFn: () => getFeedHistory(feedId),
  });

export async function getFeed(feedId: number): Promise<Feed> {
  return await axios
    .get<Feed>(`${host}/my_feeds/${feedId}`, requestSettings)
    .then((res) => res.data);
}

export const feedQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["my-feeds", feedId],
    queryFn: () => getFeed(feedId),
  });

async function getCurrentFeed(
  feedId: number,
  cursor: string,
  limit = 20,
  show_hidden = true,
): Promise<PostHydrationResponse> {
  return await axios
    .get<PostHydrationResponse>(
      `${host}/my_feeds/${feedId}/content?limit=${limit}${
        cursor ? "&cursor=" + cursor : ""
      }${show_hidden ? "" : "&show_hidden=false"}`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getCurrentFeedOptions = (
  feedId: number,
  limit = 20,
  show_hidden = true,
) =>
  infiniteQueryOptions({
    queryKey: ["feed-preview", feedId, show_hidden, limit],
    queryFn: ({ pageParam }) =>
      getCurrentFeed(feedId, pageParam, limit, show_hidden),
    initialPageParam: "",
    getNextPageParam: (lastPage) =>
      lastPage.cursor === "eof" ? undefined : lastPage.cursor,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - feed preview data doesn't change frequently
  });

export async function addPostsToFeed(input: {
  algo_id: number;
  post_urls: string[];
  current_time: boolean;
}) {
  await axios
    .post<UserHydrationResponse>(
      `${host}/api/v1/feed-management/algorithms/${input.algo_id}/insert-posts`,
      { post_urls: input.post_urls, current_time: input.current_time },
      requestSettings,
    )
    .then((res) => res.data);
  await settleFeedPreview(input.algo_id);
}

/**
 * Look at the feed again, twice, after inserting into it.
 *
 * 🔑 AN IMMEDIATE REFETCH READS THE FEED AS IT WAS. Inserts land in a ClickHouse buffer
 * that flushes to the read table on a delay, so asking straight away usually misses the row
 * that was just written — and because the preview query holds its result for five minutes
 * and does not refetch on mount, that stale answer is what the person keeps seeing. The post
 * is in the feed; the screen is wrong about it, which is worse than a visible failure.
 *
 * Extracted because it was written once for the owner's add-post path and then NOT applied
 * to the two routes added since: a collaborator adding a post, and accepting a suggestion.
 * Both looked broken for exactly this reason while having worked perfectly.
 */
async function settleFeedPreview(feedId: number) {
  await new Promise((resolve) => setTimeout(resolve, 1000));
  await queryClient.refetchQueries({ queryKey: ["feed-preview", feedId] });
  setTimeout(() => {
    // Deliberately not awaited: a late second refetch to catch rows that land after the
    // first one. `void` says so rather than leaving it floating.
    void queryClient.refetchQueries({ queryKey: ["feed-preview", feedId] });
  }, 5000);
}

export async function setEmail(email: string) {
  await axios
    .post<UserHydrationResponse>(
      `${host}/api/v1/account/set-email`,
      { email },
      requestSettings,
    )
    .then((res) => res.data);

  await queryClient.invalidateQueries({ queryKey: ["current-user"] });
}

async function hydrateUsers(input: { dids?: string[]; handles?: string[] }) {
  return await axios
    .post<UserHydrationResponse>(
      `${host}/hydrate_users`,
      input,
      requestSettings,
    )
    .then((res) => res.data);
}

async function hydrateUserDid(did: string) {
  return hydrateUsers({ dids: [did] }).then((res) => {
    const user = res.users[0];

    if (!user) {
      throw new Error("User not found");
    }

    return user;
  });
}

/**
 * The handle as the network resolves it: no leading `@`, no surrounding space.
 *
 * People paste handles the way Bluesky displays them, `@alice.bsky.social`. The
 * matcher accepts that (network-cache strips the `@` before resolving), but the
 * appview refuses it as an actor and `/hydrate_users` 500'd. The Users node then
 * called a perfectly good handle invalid, and the browser refused every save from
 * the canvas with "Feed Validation Error" (feed 35705, 2026-10-15). Normalising
 * only the lookup leaves the stored value exactly as the author wrote it.
 */
function normalizeActor(actor: string) {
  return actor.trim().replace(/^@+/, "").trim();
}

export const getHydratedUserDidQueryOptions = (did: string) => {
  const actor = normalizeActor(did);

  return queryOptions({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryKey: ["hydrated-user", actor],
    queryFn: () => hydrateUserDid(actor),
  });
};

/**
 * Several DIDs in one round trip, keyed by DID.
 *
 * `/hydrate_users` already takes a list, so a screen showing a handful of people — the
 * collaborator list — asks once rather than once per row. A DID that does not resolve is
 * simply absent from the map: callers fall back to the DID itself rather than rendering a
 * blank where a person should be.
 */
async function hydrateUsersByDid(dids: string[]): Promise<Record<string, User>> {
  if (dids.length === 0) {
    return {};
  }
  const res = await hydrateUsers({ dids });
  return Object.fromEntries(res.users.map((u) => [u.did, u]));
}

export const hydratedUsersByDidQueryOptions = (dids: string[]) => {
  // Deduped and sorted, so the cache key does not change just because the list came back
  // in a different order.
  const keys = [...new Set(dids)].sort();
  return queryOptions({
    queryKey: ["hydrated-users", keys],
    queryFn: () => hydrateUsersByDid(keys),
    enabled: keys.length > 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
  });
};

async function hydratePosts(at_uris: string[]) {
  return await axios
    .post<PostHydrationResponse>(
      `${host}/hydrate_posts`,
      { at_uris },
      requestSettings,
    )
    .then((res) => res.data);
}

export async function hydratePost(at_uri: string) {
  if (!at_uri) {
    throw new Error("No post URI provided");
  }

  const res = await hydratePosts([at_uri]);
  const post = res.posts[0];

  if (!post) {
    throw new Error("Post not found");
  }

  return post;
}

export const getHydratedPostQueryOptions = (at_uri: string) => ({
  queryKey: ["hydrated-post", at_uri],
  queryFn: () => hydratePost(at_uri),
});

export async function hydrateUserUsername(username: string) {
  const res = await hydrateUsers({ handles: [username.replace(/^@/, "")] });
  const user = res.users[0];

  if (!user) {
    throw new Error("User not found");
  }

  return user;
}

export const hydrateUserUsernameQueryOptions = (username: string) =>
  queryOptions({
    queryKey: ["hydrated-user-username", username],
    queryFn: () => hydrateUserUsername(username),
  });

export async function createFeed(form: FormData) {
  form.set("user_id", getUserId());

  // A refusal is a 400 (axios throws) -- or, from an older API build, a 2xx
  // carrying `error`. assertSaveAccepted makes the second throw like the first,
  // so neither can reach the caller's onSuccess and navigate. See save-refusal.ts.
  const feed = await axios
    .post<{ algorithm: Feed }>(
      `${host}/add_algo_image`,
      form,
      requestSetingsFormData,
    )
    .then(assertSaveAccepted);

  void queryClient.invalidateQueries({ queryKey: ["my-feeds"] });
  return feed;
}

export async function createCustomNode(options: {
  description: string;
  title: string;
  public: boolean;
  component: FilterTypes;
  component_metadata: { snapshot?: TLEditorSnapshot };
  color: string;
}) {
  const node = await axios
    .post<{
      id: number;
    }>(
      `${host}/api/v1/algorithm-components/components`,
      { ...options, name: kebabCase(options.title) },
      requestSettings,
    )
    .then((res) => res.data);

  return node;
}

/** Bring an archived node back into search. The inverse of deleting one. */
type NodeAttentionItem = {
  install_id: number;
  state: "detached" | "pending_review" | "will_detach";
  algorithm_id: number;
  algorithm_name: string | null;
  component_id: number;
  component_title: string | null;
  condition_id: number | null;
  current_version: number | null;
  latest_version: number | null;
  summary: string | null;
  dismissed: boolean;
};

/**
 * Nodes on the caller's feeds that need the owner to do something.
 *
 * `detached` is the one nobody can currently see: the node still filters
 * exactly as it did, so nothing looks wrong, but the link back to the
 * published node is gone and it can never receive an update again. The only
 * fix is for the owner to re-add the node, which is a decision with
 * consequences -- it may change what the feed matches -- so it is surfaced
 * rather than done for them.
 */
async function getNodeAttentionItems() {
  return await axios
    .get<{ items: NodeAttentionItem[] }>(
      `${host}/api/v1/algorithm-components/components/attention`,
      requestSettings,
    )
    .then((res) => res.data.items);
}

export const nodeAttentionQueryOptions = () =>
  queryOptions({
    queryKey: ["custom-node-attention"],
    queryFn: () => getNodeAttentionItems(),
  });

export type EmbeddedCopy = {
  path: string;
  component_id: number;
  name: string | null;
  embedded_version: number | null;
  latest_version: number | null;
  versions_behind: number | null;
  /** null means the comparison could not be made — never read it as "current". */
  stale: boolean | null;
};

/**
 * The nodes baked into this one, and how far behind each copy is.
 *
 * Embedding is by value: the copy is bytes, and no publish of the source can
 * reach it. That is a design gap and this does not close it — what it closes is
 * the part where the author could not tell there was anything to decide.
 */
async function getEmbeddedNodes(componentId: number) {
  return await axios
    .get<{
      component_id: number;
      embedded: EmbeddedCopy[];
      stale_count: number;
    }>(
      `${host}/api/v1/algorithm-components/components/${componentId}/embedded_nodes`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const embeddedNodesQueryOptions = (componentId: number) =>
  queryOptions({
    queryKey: ["custom-node-embedded", componentId],
    queryFn: () => getEmbeddedNodes(componentId),
  });

export type PublishReach = {
  component_id: number;
  direct_feeds: number;
  /** Feeds that only reach this node because another node has a copy baked in. */
  feeds_through_embeds: number;
  total_feeds: number;
  embedded_in: number[];
  embedded_in_count: number;
};

/**
 * How many feeds a publish of this node would rewrite, and how it gets there.
 *
 * The second number is the one nobody can see today: embedding is by value, so
 * a node can sit inside other nodes used by hundreds of feeds while showing no
 * installs of its own.
 */
async function getPublishReach(componentId: number) {
  return await axios
    .get<PublishReach>(
      `${host}/api/v1/algorithm-components/components/${componentId}/publish_reach`,
      requestSettings,
    )
    .then((res) => res.data);
}

export type ResolvedEmbed = {
  component_id: number;
  requested_version: number | null;
  intent: DependencyIntent;
  resolved_version: number | null;
  /** True when the feed will run a different version than the canvas was given. */
  moved: boolean;
  template: FilterTypes | null;
  param_schema: unknown;
  latest_version: number | null;
};

/**
 * What each embedded node resolves to right now.
 *
 * The server decides WHICH version; the editor does the expansion. The
 * browser's expander is the byte-parity reference — the Python compiler is a
 * port of it — so asking the server to pick a version keeps one expander and
 * one resolver instead of two opinions about what a feed matches.
 *
 * Without this the canvas renders whatever version it happened to fetch while
 * the feed matches whatever the resolver picks at compile time. Under
 * `compatible` those diverge the moment the source publishes.
 */
async function resolveEmbeds(
  embeds: { component_id: number; version?: number; intent?: DependencyIntent }[],
) {
  if (embeds.length === 0) return { resolved: [] as ResolvedEmbed[] };
  return await axios
    .post<{ resolved: ResolvedEmbed[] }>(
      `${host}/api/v1/algorithm-components/components/resolve_embeds`,
      { embeds },
      requestSettings,
    )
    .then((res) => res.data);
}

export const resolveEmbedsQueryOptions = (
  embeds: { component_id: number; version?: number; intent?: DependencyIntent }[],
) =>
  queryOptions({
    queryKey: ["custom-node-resolve-embeds", embeds],
    queryFn: () => resolveEmbeds(embeds),
    staleTime: 30_000,
  });

export const publishReachQueryOptions = (componentId: number) =>
  queryOptions({
    queryKey: ["custom-node-publish-reach", componentId],
    queryFn: () => getPublishReach(componentId),
    staleTime: 60_000,
  });

/**
 * Take an update that was held back for changing or removing a setting.
 *
 * Values the new version no longer has are dropped and anything new takes the
 * author's default -- which is exactly why it needs a person to agree to it.
 */
export async function acceptHeldUpdate(installId: number) {
  await axios.post(
    `${host}/api/v1/algorithm-components/components/installs/${installId}/accept_update`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["custom-node-attention"] });
}

/** Decline it and stay put. Auto-update goes off with it. */
export async function keepCurrentVersion(installId: number) {
  await axios.post(
    `${host}/api/v1/algorithm-components/components/installs/${installId}/keep_current`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["custom-node-attention"] });
}

export async function dismissNodeNotice(installId: number) {
  await axios.post(
    `${host}/api/v1/algorithm-components/components/installs/${installId}/dismiss_notice`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["custom-node-attention"] });
}

export async function restoreCustomNode(nodeId: number) {
  await axios.post(
    `${host}/api/v1/algorithm-components/components/${nodeId}/restore`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["custom-node", nodeId] });
  await queryClient.invalidateQueries({ queryKey: ["custom-nodes"] });
  await queryClient.invalidateQueries({ queryKey: ["user-custom-nodes"] });
}

export async function deleteCustomNode(nodeId: number) {
  await axios.delete(
    `${host}/api/v1/algorithm-components/components/${nodeId}`,
    requestSettings,
  );

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["user-custom-nodes"] }),
  ]);
}

export async function editCustomNode(options: {
  id: number;
  description: string;
  title: string;
  public: boolean;
  component: FilterTypes;
  color: string;
  version_message?: string;
  component_metadata: { snapshot?: TLEditorSnapshot } | undefined;
}) {
  await axios.put(
    `${host}/api/v1/algorithm-components/components/${options.id}`,
    options,
    requestSettings,
  );

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["user-custom-nodes"] }),
    queryClient.invalidateQueries({ queryKey: ["custom-node", options.id] }),
  ]);
}

export interface CustomNodeHistoryItem {
  id: number;
  avatar: string;
  color: ColorVariant;
  username?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  title: string;
  description: string;
  version_id: number;
  version_message?: string;
  public: boolean;
  component: FilterTypes;
  component_metadata?: { snapshot: TLEditorSnapshot };
  installs: number;
  unique_user_installs: number;
  favorited: boolean;
  user: User;
  graze_user_id: number;
  tags: SavedTag[];
  /**
   * Archived: hidden from other people's search, but still running on every
   * feed that installed it. Deleting a node that feeds use archives it rather
   * than removing it, so this is a normal state, not a broken one.
   */
  deprecated?: boolean;
}

async function getCustomNodeHistory(nodeId: number) {
  return await axios
    .get<CustomNodeHistoryItem[]>(
      `${host}/api/v1/algorithm-components/components/${nodeId}/history`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getCustomNodeHistoryQueryOptions = (nodeId: number) =>
  queryOptions({
    queryKey: ["custom-node-history", nodeId],
    queryFn: () => getCustomNodeHistory(nodeId),
  });

interface CustomNodePreview {
  component_id: number;
  version: number;
  filter: FilterTypes;
  parameters: FeedParameter[];
}

/**
 * Compile a custom node on the server without storing anything.
 *
 * The point is that this is the SAME code that will store the feed, so what a
 * user is shown and what ends up matching cannot drift. Substitution used to be
 * implemented twice — once here in the browser and once in Python — and the two
 * disagreeing is why some live feeds match a literal "$PARAM" string.
 */
async function previewCustomNode(options: {
  component_id: number;
  version?: number;
  values?: Record<string, unknown>;
  track_latest?: boolean;
}) {
  return await axios
    .post<CustomNodePreview>(
      `${host}/api/v1/algorithm-components/components/preview`,
      {
        component_id: options.component_id,
        version: options.version,
        values: options.values ?? {},
        track_latest: options.track_latest ?? false,
      },
      requestSettings,
    )
    .then((res) => res.data);
}

export const previewCustomNodeQueryOptions = (options: {
  component_id: number;
  version?: number;
  values?: Record<string, unknown>;
}) =>
  queryOptions({
    queryKey: [
      "custom-node-preview",
      options.component_id,
      options.version,
      options.values,
    ],
    queryFn: () => previewCustomNode(options),
    enabled: Boolean(options.component_id && options.version),
  });

export async function getCustomNode(nodeId: number) {
  return await axios
    .get<CustomNodeHistoryItem>(
      `${host}/api/v1/algorithm-components/components/${nodeId}`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getCustomNodeQueryOptions = (nodeId: number) =>
  queryOptions({
    queryKey: ["custom-node", nodeId],
    queryFn: () => getCustomNode(nodeId),
    // A 404 is an answer, not a blip: the node references a component that
    // does not exist, and the canvas warns about it. Retrying only delays that.
    retry: (failureCount, error) =>
      !(isAxiosError(error) && error.response?.status === 404) &&
      failureCount < 3,
  });

async function getUserCustomNodes() {
  return await axios
    .get<CustomNodeHistoryItem[]>(
      `${host}/api/v1/algorithm-components/users/${getUserId()}/components`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getUserCustomNodesQueryOptions = () =>
  queryOptions({
    queryKey: ["user-custom-nodes", getUserId()],
    queryFn: () => getUserCustomNodes(),
  });

interface SearchCustomNodesOptions {
  initialData?: { page: number; results: CustomNodeHistoryItem[] };
  query: string;
  sort_by?:
    | "title"
    | "install_count"
    | "unique_user_install_count"
    | "created_at"
    | "favorite_count";
  order?: "asc" | "desc";
  page?: number;
  page_size?: number;
  favorited_only?: boolean;
  user_id?: string;
  tags?: number[];
}

export async function searchCustomNodes(options: SearchCustomNodesOptions) {
  const url = new URL(`${host}/api/v1/algorithm-components/components/search`);

  url.searchParams.set("query", options.query);

  if (options.sort_by) {
    url.searchParams.set("sort_by", options.sort_by);
  }
  if (options.order) {
    url.searchParams.set("order", options.order);
  }
  if (options.page) {
    url.searchParams.set("page", options.page.toString());
  }
  if (options.page_size) {
    url.searchParams.set("page_size", options.page_size.toString());
  }
  if (options.favorited_only) {
    url.searchParams.set("favorited_only", options.favorited_only.toString());
  }
  if (options.user_id) {
    url.searchParams.set("user_id", options.user_id);
  }
  if (options.tags) {
    options.tags.forEach((tag) => {
      url.searchParams.append("tags", tag.toString());
    });
  }

  const data = await axios
    .get<CustomNodeHistoryItem[]>(url.toString(), requestSettings)
    .then((res) => res.data);

  return {
    page: options.page || 1,
    results: data,
  };
}

// async function searchCustomNodesPublic(options: SearchCustomNodesOptions) {
//   const url = new URL(
//     `${host}/api/v1/algorithm-components/components/public_search`,
//   );

//   url.searchParams.set("query", options.query);

//   if (options.sort_by) {
//     url.searchParams.set("sort_by", options.sort_by);
//   }
//   if (options.order) {
//     url.searchParams.set("order", options.order);
//   }
//   if (options.page) {
//     url.searchParams.set("page", options.page.toString());
//   }
//   if (options.page_size) {
//     url.searchParams.set("page_size", options.page_size.toString());
//   }
//   if (options.favorited_only) {
//     url.searchParams.set("favorited_only", options.favorited_only.toString());
//   }
//   if (options.user_id) {
//     url.searchParams.set("user_id", options.user_id);
//   }

//   const data = await axios
//     .get<CustomNodeHistoryItem[]>(url.toString(), requestSettings)
//     .then((res) => res.data);

//   return {
//     page: options.page || 1,
//     results: data,
//   };
// }

export const searchCustomNodesQueryOptions = (
  options: SearchCustomNodesOptions,
) =>
  infiniteQueryOptions({
    initialData: options.initialData
      ? {
          pageParams: [1],
          pages: [options.initialData],
        }
      : undefined,
    queryKey: [
      "search-custom-nodes",
      options.query,
      options.sort_by,
      options.order,
      options.page,
      options.page_size,
      options.favorited_only,
      options.user_id,
      options.tags,
    ],
    queryFn: ({ pageParam }) =>
      searchCustomNodes({
        ...options,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => (lastPage ? lastPage.page + 1 : undefined),
    initialPageParam: 1,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

export const getTopCustomNodes = () =>
  queryOptions({
    queryKey: ["search-custom-nodes", "top"],
    queryFn: () =>
      searchCustomNodes({
        query: "",
        sort_by: "install_count",
        order: "desc",
        page: 1,
        page_size: 8,
      }).then((res) => res.results),
  });

export async function editFeed(feedId: number, form: FormData): Promise<void> {
  form.set("id", `${feedId}`);
  form.set("user_id", getUserId());

  // Read the body before touching any cache: a 2xx carrying `error` is a
  // refusal, and invalidating first would refetch a feed that did not change
  // while the banner says it was not saved. See save-refusal.ts.
  assertSaveAccepted(
    await axios.post(`${host}/edit_algo_image`, form, requestSetingsFormData),
  );
  await queryClient.invalidateQueries({ queryKey: ["my-feeds"] });
  await queryClient.invalidateQueries({
    queryKey: ["feed-preview", feedId],
  });
}

export async function deleteFeed(feed: Feed): Promise<void> {
  await axios.post(
    `${host}/delete_algo`,
    {
      user_id: getUserId(),
      id: feed.id,
    },
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["my-feeds"] }),
    queryClient.invalidateQueries({ queryKey: ["my-feeds-infinite"] }),
  ]);
  queryClient.removeQueries({ queryKey: ["my-feeds", feed.id] });
}

export async function publishFeed(feedId: number): Promise<void> {
  await axios.get(`${host}/publish_algo/${feedId}`, requestSettings);
  await queryClient.invalidateQueries({ queryKey: ["my-feeds", feedId] });
}

export const LoginSuccessType = {
  AT_PROTOFANS_STRIPE: "atprotofans-stripe",
  PATREON_LINK: "patreon-link",
  /** Sent through Bluesky sign-in to pick up the co/core scope, then returned
   *  to the account page to finish connecting. See CocoreConnect. */
  COCORE_CONNECT: "cocore-connect",
  DEFAULT: "default",
} as const;

type LoginSuccessTypeValue =
  (typeof LoginSuccessType)[keyof typeof LoginSuccessType];

const CookiesKeys = {
  sessionString: "session_cookie",
  authCookie: "auth-cookie",
  authToken: "auth-token",
  userId: "user-id",
  loginSuccessType: "login-success-type",
};

export function setLoginSuccessType(type: LoginSuccessTypeValue): void {
  cookies.set(CookiesKeys.loginSuccessType, type, {
    path: "/",
    secure: true,
    sameSite: "none",
    expires: new Date(Date.now() + 5 * 60 * 1000), // 5 minutes
  });
}

export function getLoginSuccessType(): LoginSuccessTypeValue | undefined {
  return cookies.get(CookiesKeys.loginSuccessType) as
    | LoginSuccessTypeValue
    | undefined;
}

export function clearLoginSuccessType(): void {
  cookies.remove(CookiesKeys.loginSuccessType, { path: "/" });
}

export async function login(
  username: string,
  password: string,
  domain: string,
): Promise<void> {
  const creds = await axios.post<{
    session_string: string;
    user: { id: number };
  }>(
    `${host}/login`,
    { username, password, service_domain: domain },
    {
      ...requestSettings,
      headers: { "Content-Type": "application/json" },
    },
  );

  const [auth_cookie] = creds.data.session_string.split(":::");

  cookies.set(CookiesKeys.authCookie, auth_cookie, {
    path: "/",
    secure: true,
    sameSite: "none",
    expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
  });
  setUserIdCookie(creds.data.user.id);
}

export interface FeedAnalytics {
  algorithm_id: number;
  feed_view_count: number;
  post_count: number;
  post_view_count: number;
  unique_user_count: number;
  feed_like_count: number;
  view_date: string;
}

export interface CampaignAnalytic {
  view_date: string;
  view_count: number;
  unique_user_count: number;
}

export interface CampaignAnalyticsSummaryType {
  details: CampaignAnalytic[];
  summary: {
    total_views: number;
    avg_views: number;
    total_unique_user_views: number;
    avg_unique_user_views: number;
  };
}

async function getCampaignAnalytics({
  campaignId,
  algorithmId,
  postUri,
  days,
}: {
  campaignId: number[];
  algorithmId?: number[];
  postUri?: string[];
  days: number;
}) {
  return await axios
    .post<CampaignAnalyticsSummaryType>(
      `${host}/api/v1/sponsored-posts/sponsored-stats-draft`,
      {
        campaign_id: campaignId,
        algorithm_id: algorithmId,
        post_uri: postUri,
        days,
      },
      requestSettings,
    )
    .then((res) => res.data);
}

export const feedCampaignQueryOptions = ({
  campaignId,
  algorithmId,
  postUri,
  days,
}: {
  campaignId: number[];
  algorithmId?: number[];
  postUri?: string[];
  days: number;
}) =>
  queryOptions({
    queryKey: ["campaign-stats", ...[...campaignId, ...(algorithmId || [])]],
    queryFn: () =>
      getCampaignAnalytics({ campaignId, algorithmId, postUri, days }),
    retry: false,
  });

async function getUserStats({ days }: { days: number }) {
  return await axios
    .post<{
      details: Array<FeedAnalytics>;
      summary: StatsSummary;
    }>(`${host}/api/v1/stats/user_stats`, { days }, requestSettings)
    .then((res) => res.data);
}

export const userStatsQueryOptions = ({ days = 30 }: { days?: number } = {}) =>
  queryOptions({
    queryKey: ["user-stats", days],
    queryFn: () => getUserStats({ days }),
    retry: false,
  });

export interface StatsSummary {
  avg_feed_view_count: number;
  avg_post_count: number;
  avg_post_view_count: number;
  avg_unique_user_count: number;
  sum_feed_view_count: number;
  sum_post_count: number;
  sum_post_view_count: number;
  sum_unique_user_count: number;
  sum_feed_like_count: number;
  avg_feed_like_count: number;
}

async function getAlgorithmStats({
  days,
  algorithmId,
}: {
  days: number;
  algorithmId: number;
}) {
  return await axios
    .post<{
      details: Record<number, Array<FeedAnalytics>>;
      summary: StatsSummary;
    }>(
      `${host}/api/v1/stats/algorithm_stats`,
      { days, algorithm_ids: [algorithmId] },
      requestSettings,
    )
    .then((res) => {
      const details = res.data.details[algorithmId];
      const summary = res.data.summary;

      if (!details) {
        return { details: [], summary };
      }

      return { details, summary };
    });
}

export const algorithmStatsQueryOptions = ({
  days = 30,
  algorithmId,
}: {
  days?: number;
  algorithmId: number;
}) =>
  queryOptions({
    queryKey: ["algorithm-stats", days, algorithmId],
    queryFn: () => getAlgorithmStats({ days, algorithmId }),
    retry: false,
  });

export async function isAuthenticated() {
  try {
    await queryClient.ensureQueryData(userStatsQueryOptions());
    return true;
  } catch {
    queryClient.removeQueries();
    return false;
  }
}

async function getAlgorithmAsset<Metadata>(
  options:
    | {
        asset_type: "starter_pack";
        asset_parameters: {
          starter_pack_url: string;
        };
      }
    | {
        asset_type: "user_collection";
        asset_parameters: {
          actor_handle: string;
          direction: RelationshipType;
        };
      }
    | {
        asset_type: "user_list";
        asset_parameters: {
          list_url: string;
        };
      },
) {
  return await axios
    .post<{
      asset_value: string[];
      asset_metadata: Metadata | null;
    }>(`${host}/get_algorithm_asset`, options, requestSettings)
    .then((res) => res.data);
}

export const getStarterPackQueryOptions = (starterPackUrl: string) =>
  queryOptions({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryKey: ["starter-pack", starterPackUrl],
    queryFn: () =>
      getAlgorithmAsset<{
        starter_pack?: {
          uri: string;
          list: { name: string; avatar: string };
          creator: User;
          list_items_sample: { uri: string; subject: User }[];
        };
      }>({
        asset_type: "starter_pack",
        asset_parameters: {
          starter_pack_url: starterPackUrl,
        },
      }),
  });

export const getUserCollectionQueryOptions = (
  actorHandle: string,
  direction: RelationshipType,
) =>
  queryOptions({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryKey: ["user-collection", actorHandle, direction],
    queryFn: () =>
      getAlgorithmAsset<User[]>({
        asset_type: "user_collection",
        asset_parameters: {
          actor_handle: actorHandle,
          direction,
        },
      }),
  });

export const getUserListQueryOptions = (listUrl: string) =>
  queryOptions({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryKey: ["user-list", listUrl],
    queryFn: () =>
      getAlgorithmAsset<{
        list?: {
          uri: string;
          name: string;
          avatar: string;
          description: string;
          creator: User;
        };
        items?: { uri: string; subject: User }[];
      }>({
        asset_type: "user_list",
        asset_parameters: {
          list_url: listUrl,
        },
      }),
  });

export async function hidePost(input: {
  at_uri: string;
  algo_id: number;
}): Promise<void> {
  await axios.post(`${host}/hide_post`, input, requestSettings);
  await queryClient.invalidateQueries({ queryKey: ["feed-preview"] });
}

export async function hidePostForAllFeeds(input: {
  at_uri: string;
}): Promise<void> {
  await axios.post(`${host}/hide_post`, input, requestSettings);
  await queryClient.invalidateQueries({ queryKey: ["feed-preview"] });
}

export async function unHidePost(input: {
  at_uri: string;
  algo_id: number;
}): Promise<void> {
  await axios.post(`${host}/unhide_post`, input, requestSettings);
  await queryClient.invalidateQueries({ queryKey: ["feed-preview"] });
}

export async function clearFeed(algo_id: number): Promise<void> {
  await axios.delete(
    `${host}/api/v1/algorithm-management/clear-feed/${algo_id}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["feed-preview"] });
}

interface BackfillStatus {
  status: "none" | "queued" | "running" | "done" | "error" | "skipped";
  /**
   * Whether a job is actually live, as decided by the server — the same
   * predicate the backfill and rebuild routes gate on. Prefer this over
   * `status` for enabling/disabling UI: `status` alone stays "queued" for the
   * full 7-day record TTL when a job is lost before the worker claims it,
   * which would disable the buttons for a week while the routes behind them
   * would accept the request. Absent on the POST responses, which return the
   * freshly-enqueued record rather than a re-read of it.
   */
  in_progress?: boolean;
  updated_at?: string;
  warning?: string;
  message?: string;
  /** Progress, written by the replay worker as it goes. */
  archives_total?: number;
  archives_done?: number;
  /**
   * Matches for THIS feed — but only when `per_feed_counts` is true.
   *
   * A refill job replays every feed in its message at once, and the worker used
   * to write the job-wide total into every participating feed's record. Feed
   * 11597 read 3,400,257 for a day on which ~4,082 rows actually reached it.
   * Never show this number without checking the flag below.
   */
  matches_emitted?: number;
  /**
   * Whether `matches_emitted` describes this feed alone.
   *
   * ABSENT means a worker older than the fix, where it is the whole job's
   * figure. False means the fan-out lane could not attribute the matches — its
   * scan Lambda deploys separately and may predate the change — and said so
   * rather than inventing a per-feed number.
   */
  per_feed_counts?: boolean;
  /** The whole job's matches across every feed in it, for context. */
  job_matches_emitted?: number;
  /** How the job was started: "new_feed", "reactivation", "rebuild", "manual". */
  source?: string;
}

export async function backfillFeed(algo_id: number): Promise<BackfillStatus> {
  const { data } = await axios.post<BackfillStatus>(
    `${host}/api/v1/algorithm-management/backfill/${algo_id}`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["feed-preview"] });
  await queryClient.invalidateQueries({ queryKey: ["backfill-status", algo_id] });
  return data;
}

export async function rebuildFeed(algo_id: number): Promise<BackfillStatus> {
  const { data } = await axios.post<BackfillStatus>(
    `${host}/api/v1/algorithm-management/rebuild/${algo_id}`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["feed-preview"] });
  await queryClient.invalidateQueries({ queryKey: ["backfill-status", algo_id] });
  return data;
}

export async function getBackfillStatus(
  algo_id: number,
): Promise<BackfillStatus> {
  const { data } = await axios.get<BackfillStatus>(
    `${host}/api/v1/algorithm-management/backfill-status/${algo_id}`,
    requestSettings,
  );
  return data;
}

export async function removeStickyPostFromCampaign(
  campaignId: number,
  stickyPostId: number,
) {
  await axios.delete(
    `${host}/api/v1/campaigns/${campaignId}/sticky-posts/${stickyPostId}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({
    queryKey: ["pending-posts-for-campaign", campaignId],
  });
}

interface GetStickyPostsOptions {
  algo_id: number;
  page?: number;
  page_size?: number;
  sort_by?: string;
  order?: string;
}

async function getStickyPostsItem(options: GetStickyPostsOptions) {
  const url = new URL(
    `${host}/api/v1/feed-management/sticky-posts/${options.algo_id}`,
  );

  url.searchParams.set("page", options.page?.toString() || "1");
  url.searchParams.set("page_size", options.page_size?.toString() || "10");
  if (options.sort_by) {
    url.searchParams.set("sort_by", options.sort_by);
  }
  if (options.order) {
    url.searchParams.set("order", options.order);
  }

  const page = await axios.get<{
    sticky_posts: StickyPost[];
    pagination: {
      has_next: boolean;
      has_prev: boolean;
      page: number;
      page_size: number;
      total_items: number;
      total_pages: number;
    };
  }>(url.toString(), requestSettings);

  return page.data;
}

async function getStickyPosts(options: GetStickyPostsOptions) {
  const firstPage = await getStickyPostsItem({
    ...options,
    page: 1,
    page_size: 10,
  });

  if (!firstPage.pagination.has_next) {
    return firstPage.sticky_posts;
  }

  const pages = await Promise.all(
    Array.from({ length: firstPage.pagination.total_pages }, (_, index) =>
      getStickyPostsItem({
        ...options,
        page: index + 1,
      }).then((res) => res.sticky_posts),
    ),
  );

  return pages.flat();
}

export const getStickyPostsOptions = (options: GetStickyPostsOptions) =>
  queryOptions({
    queryKey: [
      "sticky-posts-infinite",
      options.algo_id,
      options.page,
      options.page_size,
      options.sort_by,
      options.order,
    ],
    queryFn: () => getStickyPosts(options),
  });

interface StickyPostData {
  algo_id?: number;
  post_url: string;
  is_active: boolean;
  expiration?: Date;
  sticky_type: "pinned" | "injected";
  order?: number;
}

export async function addStickyPost(payload: StickyPostData) {
  const expirationInUtcSeconds = payload.expiration
    ? Math.floor(payload.expiration.getTime() / 1000)
    : undefined;

  await axios.post(
    `${host}/api/v1/feed-management/sticky-posts`,
    {
      algo_id: payload.algo_id,
      post_url: payload.post_url,
      is_active: payload.is_active,
      sticky_type: payload.sticky_type,
      order: payload.order,
      stopping_criteria: expirationInUtcSeconds
        ? {
            expiration_time: expirationInUtcSeconds,
          }
        : undefined,
    },
    requestSettings,
  );

  await queryClient.invalidateQueries({
    queryKey: ["sticky-posts", payload.algo_id],
  });

  void queryClient.invalidateQueries({
    queryKey: ["feed-preview", payload.algo_id],
  });
}

interface EditStickyPostData {
  algo_id: number;
  sticky_post_id: number;
  /**
   * Which copy to act on. (algo_id, sticky_post_id) is not unique -- a feed can
   * hold the same post as both `pinned` and `injected`. `assoc_id` names one row
   * exactly; `sticky_type` narrows to the rows of that type. Neither is written
   * to the row. Given neither, the server picks a single row on its own.
   */
  assoc_id?: number;
  sticky_type?: string;
  is_active?: boolean;
  order?: number;
}

export async function editStickyPost(payload: EditStickyPostData) {
  const url = new URL(
    `${host}/api/v1/feed-management/sticky-posts/${payload.algo_id}/${payload.sticky_post_id}`,
  );

  if (typeof payload.is_active === "boolean") {
    url.searchParams.set("is_active", payload.is_active.toString());
  }

  if (payload.sticky_type) {
    url.searchParams.set("sticky_type", payload.sticky_type);
  }

  if (payload.order) {
    url.searchParams.set("order", payload.order.toString());
  }

  await axios.put(url.toString(), payload, requestSettings);
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["sticky-posts", payload.algo_id],
    }),
    queryClient.invalidateQueries({
      queryKey: ["feed-preview", payload.algo_id],
    }),
  ]);
}

export async function deleteStickyPost(payload: {
  algo_id: number;
  sticky_post_id: number;
  /** See EditStickyPostData: names WHICH copy to remove. */
  assoc_id?: number;
  sticky_type?: string;
}) {
  const url = new URL(
    `${host}/api/v1/feed-management/sticky-posts/${payload.algo_id}/${payload.sticky_post_id}`,
  );

  // Without one of these the server removes a single association of its own
  // choosing, which may not be the copy the reader clicked.
  if (payload.assoc_id !== undefined) {
    url.searchParams.set("assoc_id", payload.assoc_id.toString());
  } else if (payload.sticky_type) {
    url.searchParams.set("sticky_type", payload.sticky_type);
  }

  await axios.delete(url.toString(), requestSettings);

  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["sticky-posts", payload.algo_id],
    }),
    queryClient.invalidateQueries({
      queryKey: ["feed-preview", payload.algo_id],
    }),
  ]);
}

async function getExternalFeedsForUser(input: { user_id: string }) {
  return await axios
    .post<{
      algorithms: ExternalFeed[];
    }>(`${host}/external_algos`, input, requestSettings)
    .then((res) => res.data);
}

export const externalFeedsForUserQueryOptions = (userId = getUserId()) =>
  queryOptions({
    queryKey: ["hydrated-user", userId],
    queryFn: () => getExternalFeedsForUser({ user_id: userId }),
  });

export async function migrateFeed(input: {
  feed_uri: string;
  algorithm_manifest: Manifest;
  order: SortingOption;
  custom_sort_settings: SortingParams | undefined;
}) {
  return await axios
    .post<Feed>(
      `${host}/migrate_algo`,
      { ...input, user_id: getUserId() },
      requestSettings,
    )
    .then((res) => res.data);
}

export async function completeMigration(input: {
  algo_id: number;
}): Promise<void> {
  await axios.post(
    `${host}/complete_migration`,
    { ...input, user_id: getUserId() },
    requestSettings,
  );
  await queryClient.invalidateQueries({
    queryKey: ["my-feeds", input.algo_id],
  });
}

export async function getCurrentUser() {
  return await axios
    .get<User>(`${host}/current_user`, requestSettings)
    .then((res) => res.data);
}

export const getCurrentUserQueryOptions = () =>
  queryOptions({
    queryKey: ["current-user"],
    queryFn: () => getCurrentUser(),
  });

export async function setFeedPublic(algo_id: number, isPublic: boolean) {
  await axios.get(
    `${host}/api/v1/algorithm-management/set-publicity/${algo_id}/${isPublic}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["my-feeds", algo_id] });
}

export async function getPublicFeed(algo_id: number) {
  return await axios
    .get<PublicFeed>(
      `${host}/api/v1/algorithm-search/algos-draft/${algo_id}`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getPublicFeedQueryOptions = (algo_id: number) =>
  queryOptions({
    queryKey: ["public-feed", algo_id],
    queryFn: () => getPublicFeed(algo_id),
  });

/**
 * The feed's latest Feed Pulse, or null when there is none to show (the
 * endpoint 404s for private feeds, feeds not opted in, and feeds that have
 * never pulsed). Public; no session needed.
 */
async function getFeedPulse(algo_id: number): Promise<FeedPulse | null> {
  try {
    return await axios
      .get<FeedPulse>(
        `${host}/api/v1/algorithm-search/algos/${algo_id}/pulse`,
        requestSettings,
      )
      .then((res) => res.data);
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 404) {
      return null;
    }
    throw e;
  }
}

export const getFeedPulseQueryOptions = (algo_id: number) =>
  queryOptions({
    queryKey: ["feed-pulse", algo_id],
    queryFn: () => getFeedPulse(algo_id),
    staleTime: 5 * 60_000,
  });

async function getFeedPulseStatus(algo_id: number) {
  return await axios
    .get<FeedPulseStatus>(
      `${host}/api/v1/cocore/feeds/${algo_id}/pulse-status`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getFeedPulseStatusQueryOptions = (algo_id: number) =>
  queryOptions({
    queryKey: ["feed-pulse-status", algo_id],
    queryFn: () => getFeedPulseStatus(algo_id),
    // While a pulse is being written (job queued/running) poll until it lands;
    // the worker ticks every 60s and a model call takes 10s to 3 minutes.
    refetchInterval: (query) => {
      const state = query.state.data?.last_job?.state;
      return state === "queued" || state === "running" ? 10_000 : false;
    },
  });

/**
 * Write yesterday's pulse for this feed now instead of waiting for the 06:20
 * UTC run. Called the moment an owner turns Feed Pulse on, and from the owner
 * view's "Write today's pulse" button. Idempotent per day on the server: an
 * existing pulse is left alone, a failed one is retried.
 */
export async function requestFeedPulseNow(algo_id: number) {
  const result = await axios
    .post<FeedPulseNowResult>(
      `${host}/api/v1/cocore/feeds/${algo_id}/pulse-now`,
      {},
      requestSettings,
    )
    .then((res) => res.data);
  await queryClient.invalidateQueries({
    queryKey: ["feed-pulse-status", algo_id],
  });
  return result;
}

async function getCocoreStatus() {
  return await axios
    .get<CocoreStatus>(`${host}/api/v1/cocore/status`, requestSettings)
    .then((res) => res.data);
}

export const getCocoreStatusQueryOptions = () =>
  queryOptions({
    queryKey: ["cocore-status"],
    queryFn: () => getCocoreStatus(),
  });

/** Error body the co/core endpoints return on a 4xx: `{detail: {code, message, signin_url?}}`. */
interface CocoreErrorDetail {
  code: string;
  message: string;
  signin_url?: string;
  keys_url?: string;
}

export function cocoreErrorDetail(e: unknown): CocoreErrorDetail | null {
  if (
    isAxiosError(e) &&
    e.response?.data &&
    typeof e.response.data === "object" &&
    "detail" in e.response.data &&
    typeof (e.response.data as { detail: unknown }).detail === "object"
  ) {
    return (e.response.data as { detail: CocoreErrorDetail }).detail;
  }
  return null;
}

/** Connect co/core: Graze asks the owner's own PDS to mint a key for their DID.
 *  No Graze credential takes part; the PDS proves DID control to co/core. */
export async function connectCocore() {
  const status = await axios
    .post<CocoreStatus>(`${host}/api/v1/cocore/connect`, {}, requestSettings)
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["cocore-status"] });
  return status;
}

/** Begin the co/core device pairing: returns where to send the browser. */
export async function startCocorePairing() {
  const pairing = await axios
    .post<{ state: "pending" } & CocorePairing>(
      `${host}/api/v1/cocore/connect/start`,
      {},
      requestSettings,
    )
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["cocore-status"] });
  return pairing;
}

async function pollCocorePairing() {
  return await axios
    .get<CocorePairPoll>(`${host}/api/v1/cocore/connect/poll`, requestSettings)
    .then((res) => res.data);
}

/** Polls every 3s while a pairing is pending; the component stops it on a terminal state. */
export const pollCocorePairingQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: ["cocore-pairing"],
    queryFn: () => pollCocorePairing(),
    enabled,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === "pending" || state === "unavailable" ? 3000 : false;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

export async function cancelCocorePairing() {
  await axios.post(`${host}/api/v1/cocore/connect/cancel`, {}, requestSettings);
  await queryClient.invalidateQueries({ queryKey: ["cocore-status"] });
  await queryClient.invalidateQueries({ queryKey: ["cocore-pairing"] });
}

export async function pasteCocoreKey(api_key: string) {
  const status = await axios
    .post<CocoreStatus>(
      `${host}/api/v1/cocore/connect/paste`,
      { api_key },
      requestSettings,
    )
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["cocore-status"] });
  return status;
}

export async function verifyCocore() {
  const status = await axios
    .post<CocoreStatus>(
      `${host}/api/v1/cocore/connect/verify`,
      {},
      requestSettings,
    )
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["cocore-status"] });
  return status;
}

export async function disconnectCocore() {
  const status = await axios
    .delete<CocoreStatus>(`${host}/api/v1/cocore/connect`, requestSettings)
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["cocore-status"] });
  return status;
}

export interface GetPublicFeedOptions {
  page?: number;
  page_size?: number;
  sort_by?: "name" | "created_at" | "updated_at" | "complexity" | "popularity";
  order?: "asc" | "desc";
  search?: string;
  user_id?: string;
  algo_id?: number;
  days: number;
  initialData?: GetPublicFeedResponse;
}

export interface SearchResult {
  algorithm: PublicFeed;
  username: string;
  account_bio?: string;
  user: User;
}

interface GetPublicFeedResponse {
  total_items: number;
  page: number;
  page_size: number;
  results: Array<SearchResult>;
}

export async function getPublicFeeds(options: GetPublicFeedOptions) {
  const url = new URL(`${host}/api/v1/algorithm-search/algos-draft`);

  url.searchParams.set("page", options.page?.toString() || "1");
  url.searchParams.set("page_size", options.page_size?.toString() || "10");
  url.searchParams.set("sort_by", options.sort_by || "updated_at");
  url.searchParams.set("order", options.order || "desc");
  url.searchParams.set("search_term", options.search || "");
  url.searchParams.set("days", options.days.toString());

  if (options.user_id) {
    url.searchParams.set("user_id", options.user_id);
  }

  if (options.algo_id) {
    url.searchParams.set("algo_id", options.algo_id.toString());
  }

  return await axios
    .get<GetPublicFeedResponse>(url.toString(), requestSettings)
    .then((res) => res.data);
}

export const getPublicFeedsQueryOptions = (
  options: Partial<GetPublicFeedOptions>,
) => {
  const queryKey = [
    "public-feeds",
    options.page,
    options.sort_by,
    options.page_size,
    options.search,
    options.user_id,
    options.algo_id,
    options.days,
  ];

  return infiniteQueryOptions({
    initialData: options.initialData
      ? {
          pageParams: [1],
          pages: [options.initialData],
        }
      : undefined,
    queryKey,
    queryFn: ({ pageParam }) =>
      getPublicFeeds({
        days: 30,
        ...options,
        page: pageParam,
      }),
    initialPageParam: options.page || 1,
    getNextPageParam: (lastPage) => (lastPage ? lastPage.page + 1 : undefined),
  });
};

export async function getMonetizedFeeds(options: GetPublicFeedOptions) {
  const url = new URL(`${host}/api/v1/campaigns/algos-draft`);

  url.searchParams.set("page", options.page?.toString() || "1");
  url.searchParams.set("page_size", options.page_size?.toString() || "10");
  url.searchParams.set("sort_by", options.sort_by || "popularity");
  url.searchParams.set("order", options.order || "desc");
  url.searchParams.set("search_term", options.search || "");
  url.searchParams.set("days", options.days.toString());

  if (options.user_id) {
    url.searchParams.set("user_id", options.user_id);
  }

  if (options.algo_id) {
    url.searchParams.set("algo_id", options.algo_id.toString());
  }

  return await axios
    .get<GetPublicFeedResponse>(url.toString(), requestSettings)
    .then((res) => res.data);
}

export const getMonetizedFeedsQueryOptions = (
  options: Partial<GetPublicFeedOptions>,
) => {
  const queryKey = [
    "monetized-feeds",
    options.page,
    options.sort_by,
    options.page_size,
    options.search,
    options.user_id,
    options.algo_id,
    options.days,
  ];

  return infiniteQueryOptions({
    queryKey,
    initialData: options.initialData
      ? {
          pageParams: [1],
          pages: [options.initialData],
        }
      : undefined,
    queryFn: ({ pageParam }) =>
      getMonetizedFeeds({
        days: 30,
        ...options,
        page: pageParam,
      }),
    initialPageParam: options.page || 1,
    getNextPageParam: (lastPage) => (lastPage ? lastPage.page + 1 : undefined),
  });
};

export async function increaseCampaignImpressionsBudget(options: {
  campaignId: number;
  algoId: number;
  impressions: number;
}) {
  await axios.post(
    `${host}/api/v1/campaigns/${options.campaignId}/algorithms/${options.algoId}/budget`,
    { additional_impressions: options.impressions },
    requestSettings,
  );

  await queryClient.invalidateQueries({
    queryKey: ["campaign", options.campaignId],
  });
}

async function getPendingPostsForCampaign(campaignId: number) {
  const res = await axios.get<
    (StickyPostPerformanceForAlgo & { pending_algo_ids: number[] })[]
  >(`${host}/api/v1/campaigns/${campaignId}/pending`, requestSettings);
  const data = res.data;

  const posts = await hydratePosts(data.map((p) => p.uri));

  const withPost = [];

  for (const p of data) {
    const post = posts.posts.find((post) => post.uri === p.uri);

    if (post) {
      withPost.push({ ...p, post });
    }
  }

  return withPost;
}

export type PendingCampaignPost = Awaited<
  ReturnType<typeof getPendingPostsForCampaign>
>[number];

export const getPendingPostsForCampaignQueryOptions = (campaignId: number) =>
  queryOptions({
    queryKey: ["pending-posts-for-campaign", campaignId],
    queryFn: () => getPendingPostsForCampaign(campaignId),
  });

async function getPendingPostsForCampaignAlgo(
  campaignId: number,
  algoId: number,
) {
  const res = await axios.get<
    (StickyPostPerformanceForAlgo & { pending_algo_ids: number[] })[]
  >(
    `${host}/api/v1/campaigns/${campaignId}/algorithms/${algoId}/pending`,
    requestSettings,
  );
  const data = res.data;

  const posts = await hydratePosts(data.map((p) => p.uri));

  return data.map((p) => ({
    ...p,
    post: posts.posts.find((post) => post.uri === p.uri)!,
  }));
}

export const getPendingPostsForCampaignAlgoQueryOptions = (
  campaignId: number,
  algoId: number,
) =>
  queryOptions({
    queryKey: ["pending-posts-for-campaign-algo", campaignId, algoId],
    queryFn: () => getPendingPostsForCampaignAlgo(campaignId, algoId),
  });

export async function allowStickPostOnCampaign(
  campaignId: number,
  stickyPostId: number,
  algoId: number,
) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/sticky-posts/${stickyPostId}/allow/${algoId}`,
    {},
    requestSettings,
  );

  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["pending-posts-for-campaign", campaignId],
    }),
    queryClient.invalidateQueries({
      queryKey: ["campaign", campaignId],
    }),
  ]);
}

export async function denyStickPostOnCampaign(
  campaignId: number,
  stickyPostId: number,
  algoId: number,
) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/sticky-posts/${stickyPostId}/deny/${algoId}`,
    {},
    requestSettings,
  );

  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["pending-posts-for-campaign", campaignId],
    }),
    queryClient.invalidateQueries({
      queryKey: ["campaign", campaignId],
    }),
  ]);
}

export interface GetCampaignHistoryOptions {
  page?: number;
  state?: CampaignState;
  algo?: number;
}

export interface GetCampaignHistoryItem {
  id: number;
  algorithm: Feed;
  algorithm_user: User;
  campaign: Campaign;
  campaign_user: User;
  cpm_cents: number;
  cpm_inflight: number;
  impressions_budget: number;
  impressions_to_date: number;
  state: CampaignState;
  metadata?: {
    reason?: string;
  };
}

async function getCampaignHistory(options: GetCampaignHistoryOptions) {
  return await axios
    .post<{
      results: Array<GetCampaignHistoryItem>;
      total_pages: number;
    }>(
      `${host}/api/v1/campaigns/algorithm_campaigns/search`,
      {
        page: options.page,
        state: options.state ? [options.state] : undefined,
        algorithm_ids: options.algo ? [options.algo] : undefined,
      },
      requestSettings,
    )
    .then((res) => res.data);
}

export const getCampaignHistoryQueryOptions = (
  options: Partial<GetCampaignHistoryOptions>,
) => {
  const queryKey = [
    "campaign-history",
    options.page,
    options.state,
    options.algo,
  ];

  return queryOptions({
    queryKey,
    queryFn: () => getCampaignHistory(options),
    placeholderData: keepPreviousData,
  });
};

export interface StickyPostPerformance {
  post_uri: string;
  all_time_total_views: number;
  all_time_unique_user_views: number;
  today_total_views: number;
  today_unique_user_views: number;
}

async function getStickyPostsPerformance(feedId: number) {
  return axios
    .get<{
      sticky_posts_performance: StickyPostPerformance[];
    }>(`${host}/api/v1/sticky-posts/performance/${feedId}`, requestSettings)
    .then((res) => res.data.sticky_posts_performance);
}

export const stickyPostsPerformanceQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["sticky-posts-performance", feedId],
    queryFn: () => getStickyPostsPerformance(feedId),
  });

export interface FeedEngagementResult {
  url: string;
  action_identifier: string;

  /** Total likes across all Bluesky */
  like_count: number;
  /** Total replies across all Bluesky */
  reply_count: number;
  /** Total reposts across all Bluesky */
  repost_count: number;

  /** Likes from our feed readers (anywhere) */
  reader_like_count: number;
  /** Replies from our feed readers (anywhere) */
  reader_reply_count: number;
  /** Reposts from our feed readers (anywhere) */
  reader_repost_count: number;
  /** Engagement from our feed readers (anywhere) */
  interaction_engagement: number;

  /** Replies triggered by THIS feed */
  interaction_reply_count: number;
  /** Likes triggered by THIS feed */
  interaction_like_count: number;
  /** Reposts triggered by THIS feed */
  interaction_repost_count: number;
  /** Quote posts triggered by THIS feed */
  interaction_quote_count: number;

  interaction_seen_count: number;
  clickthrough_item_count: number;
  clickthrough_author_count: number;
  clickthrough_reposter_count: number;
  clickthrough_embed_count: number;
  request_less_count: number;
  request_more_count: number;
  total_interactions: number;
  engagement_rate: number;
  post: Post;
}

export type FeedEngagementSort =
  | "all_interactions"
  | "clickthrough_author"
  | "clickthrough_embed"
  | "clickthrough_item"
  | "clickthrough_reposter"
  | "engagement_rate"
  | "feedback_score"
  | "global_engagement"
  | "global_like_count"
  | "global_reply_count"
  | "global_repost_count"
  | "interaction_plus_global"
  | "interaction_plus_reader"
  | "reader_engagement"
  | "reader_like_count"
  | "reader_reply_count"
  | "reader_repost_count"
  | "request_less"
  | "request_more"
  | "seen_count"
  | "total_clicks"
  | "total_engagement"
  | "total_interactions";

interface GetFeedEngagementOptions {
  limit?: number;
  sort_by?: FeedEngagementSort;
  offset?: number;
  order?: "asc" | "desc";
}

async function getFeedEngagement(
  feedId: number,
  options: GetFeedEngagementOptions,
) {
  const url = new URL(`${host}/api/v1/feed-content/engagement/${feedId}`);

  if (options.sort_by) {
    url.searchParams.set("sort_by", options.sort_by);
  }

  if (options.order) {
    url.searchParams.set("order", options.order);
  }

  if (options.limit) {
    url.searchParams.set("limit", options.limit.toString());
  }

  if (options.offset) {
    url.searchParams.set("offset", options.offset.toString());
  }

  return axios
    .get<{
      results: FeedEngagementResult[];
    }>(url.toString(), requestSettings)
    .then((res) => res.data.results);
}

export const feedEngagementQueryOptions = (
  feedId: number,
  {
    sort_by = "reader_like_count",
    limit = 10,
    order = "desc",
  }: GetFeedEngagementOptions,
) =>
  infiniteQueryOptions({
    queryKey: ["feed-engagement", feedId, sort_by, limit, order, order],
    queryFn: ({ pageParam }) =>
      getFeedEngagement(feedId, { sort_by, limit, offset: pageParam, order }),
    getNextPageParam: (lastPage, pages) => {
      return pages.length * lastPage.length;
    },
    initialPageParam: 0,
  });

export interface StickyPostPerformanceForAlgo {
  uri: string;
  id: number;
  details: {
    all_time_total_views: number;
    all_time_unique_user_views: number;
    today_total_views: number;
    today_unique_user_views: number;
    total_clicks_observed?: number;
  };
}

async function getSponsoredPostsPerformance(
  campaignId: number,
  feedId: number,
) {
  return axios
    .get<{
      sticky_posts: StickyPostPerformanceForAlgo[];
    }>(
      `${host}/api/v1/campaigns/${campaignId}/algos/${feedId}`,
      requestSettings,
    )
    .then((res) => res.data.sticky_posts);
}

export const sponsoredPostsPerformanceQueryOptions = (
  campaignId: number,
  feedId: number,
) =>
  queryOptions({
    queryKey: ["sponsored-posts-performance", campaignId, feedId],
    queryFn: () => getSponsoredPostsPerformance(campaignId, feedId),
  });

export interface StickyPostPerformanceEntry {
  date: string;
  daily_total_views: number;
  daily_unique_user_views: number;
}

interface GetStickyPostPerformanceOptions {
  feedId: number;
  uri: string;
}

async function getStickyPostPerformance(
  options: GetStickyPostPerformanceOptions,
) {
  const url = new URL(
    `${host}/api/v1/sticky-posts/performance/${options.feedId}/detail`,
  );

  url.searchParams.set("post_uri", options.uri.toString());

  return axios
    .get<{
      performance_timeline: StickyPostPerformanceEntry[];
    }>(url.toString(), requestSettings)
    .then((res) => res.data.performance_timeline);
}

export const stickyPostPerformanceQueryOptions = (
  options: GetStickyPostPerformanceOptions,
) =>
  queryOptions({
    queryKey: ["sticky-post-performance", options.feedId, options.uri],
    queryFn: () => getStickyPostPerformance(options),
  });
export interface SubscriptionUser {
  user_id: number;
  name: string;
  username: string;
  avatar: string;
  display_name: string;
}

export interface SubscriberList {
  id: number;
  display_name: string;
  list_type: "memberships" | "blocklists";
  user_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  algorithms: Array<Feed>;
  subscribers: Array<SubscriptionUser>;
}

interface GetSubscriberListsResponse {
  subscriber_lists: Array<SubscriberList>;
}

async function getSubscriberLists() {
  return axios
    .get<GetSubscriberListsResponse>(
      `${host}/api/v1/feed-management/subscriber-lists`,
      requestSettings,
    )
    .then((res) => res.data.subscriber_lists);
}

export const getSubscriberListsQueryOptions = () =>
  queryOptions({
    queryKey: ["subscriber-lists"],
    queryFn: () => getSubscriberLists(),
  });

async function getSubscriberList(listId: number) {
  return axios
    .get<SubscriberList>(
      `${host}/api/v1/feed-management/subscriber-lists/${listId}`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getSubscriberListQueryOptions = (listId: number) =>
  queryOptions({
    queryKey: ["subscriber-list", listId],
    queryFn: () => getSubscriberList(listId),
  });

export async function acceptLicense(license: string, version: string) {
  await axios.post<SubscriberList>(
    `${host}/api/v1/account/accept-license`,
    { license_type: license, license_version: version },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["current-user"] });
}

interface CreateSubscriberListInput {
  display_name: string;
  usernames: string[];
  list_type: "memberships" | "blocklists";
}

export async function createSubscriberList(input: CreateSubscriberListInput) {
  await axios.post<SubscriberList>(
    `${host}/api/v1/feed-management/subscriber-lists`,
    { ...input, user_id: getUserId() },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["subscriber-lists"] });
}

export async function deleteSubscriberList(listId: number) {
  await axios.delete(
    `${host}/api/v1/feed-management/subscriber-lists/${listId}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["subscriber-lists"] });
}

export async function addSubscriberToList(listId: number, username: string) {
  await axios.post(
    `${host}/api/v1/feed-management/subscriber-lists/${listId}/subscribers`,
    { username: username.replace(/^@/, "") },
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["subscriber-lists"] }),
    queryClient.invalidateQueries({
      queryKey: ["subscriber-list", listId],
    }),
  ]);
}

export async function removeSubscriberFromList(
  listId: number,
  username: string,
) {
  await axios.delete(
    `${host}/api/v1/feed-management/subscriber-lists/${listId}/subscribers`,
    { data: { username }, ...requestSettings },
  );
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["subscriber-lists"] }),
    queryClient.invalidateQueries({
      queryKey: ["subscriber-list", listId],
    }),
  ]);
}

export async function addSubscriberListToFeed(feedId: number, listId: number) {
  await axios.post(
    `${host}/api/v1/feed-management/algorithms/${feedId}/subscriber-lists/${listId}`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["my-feeds", feedId] });
}

export async function removeSubscriberListFromFeed(
  feedId: number,
  listId: number,
) {
  await axios.delete(
    `${host}/api/v1/feed-management/algorithms/${feedId}/subscriber-lists/${listId}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["my-feeds", feedId] });
}

async function getTopContent({
  facet,
  algo_id,
  day,
}: {
  facet: "readers" | "global";
  algo_id: number;
  day?: string;
}) {
  const url = new URL(`${host}/api/v1/feed-content/${facet}/${algo_id}`);

  if (day) {
    url.searchParams.set("day_str", day);
  }

  return await axios
    .get<{
      results: TopContentResponse[];
    }>(url.toString(), requestSettings)
    .then((res) => res.data.results);
}

export const getTopContentQueryOptions = (
  facet: "readers" | "global",
  algo_id: number,
  day?: string,
) =>
  queryOptions({
    queryKey: ["top-content", facet, algo_id, day],
    queryFn: () => getTopContent({ facet, algo_id, day }),
  });

async function searchStarterPacks({
  term,
  type,
}: {
  term: string;
  type: "list" | "starter-pack";
}) {
  return await axios
    .post<{
      starter_packs: StarterPackRecord[];
      lists: ListRecord[];
    }>(
      `${host}/api/v1/algorithm-templates/starter-packs-list-search`,
      {
        term,
        include_starter_packs: type === "starter-pack",
        include_lists: type === "list",
      },
      requestSettings,
    )
    .then((res) =>
      type === "starter-pack"
        ? res.data.starter_packs.map((p) => ({ ...p, term }))
        : res.data.lists.map((p) => ({ ...p, term })),
    );
}

export const searchStarterPacksQueryOptions = (input: {
  term: string;
  type: "list" | "starter-pack";
}) =>
  queryOptions({
    queryKey: ["search-starter-packs", input.term, input.type],
    queryFn: () => searchStarterPacks(input),
  });

export async function getAlgorithmTemplate({ term }: { term: string }) {
  return await axios
    .post<{
      manifest: Manifest;
      // The generator also names and describes the feed. These were previously
      // dropped on the floor, leaving the user to retype what it had produced.
      name?: string;
      slug?: string;
      description?: string;
    }>(`${host}/api/v1/algorithm-templates/topic`, { term }, requestSettings)
    .then((res) => res.data);
}

/**
 * One custom node the designer considered, and why.
 *
 * Surfaced because an amendment that installs a marketplace node is a bigger
 * commitment than one that adds a keyword: the author is taking on someone
 * else's filter, and should be able to see whose and how many feeds already run
 * it before accepting.
 */
interface DesignTool {
  component_id: number;
  name: string;
  title: string;
  description: string;
  version_id?: number;
  install_count: number;
  score?: number;
  reason?: string;
  parameters: {
    name: string;
    type: string;
    description: string;
    example?: unknown;
  }[];
}

interface DesignChange {
  kind: "added" | "removed";
  /** A one-line summary of the condition, with the operator's arguments in it. */
  label: string;
  is_custom_node: boolean;
  /**
   * The condition itself, so a client can draw it as the node it is.
   *
   * Optional because `label` predates it: an `api` that does not send this is
   * not broken, it just gets the summary line instead of a block.
   */
  condition?: FilterTypes;
}

export interface FeedAmendment {
  manifest: Manifest;
  summary: string;
  /** Leaf-level diff, computed server-side against the manifest that was sent. */
  changes: DesignChange[];
  described_changes: string[];
  stats: {
    before: number;
    after: number;
    added: number;
    removed: number;
    kept: number;
  };
  unchanged: boolean;
  structure_changed: boolean;
  /** Things to look at before accepting. Never blocking. */
  warnings: string[];
  tools_used: DesignTool[];
  tools_offered: DesignTool[];
  intent?: "add" | "remove" | "modify" | "mixed";
}

/**
 * Propose a change to an existing design, without applying it.
 *
 * The counterpart to `getAlgorithmTemplate`, which always returns a whole new
 * feed. This returns a proposal over the manifest it was given, so the caller
 * can show what changed and let the author accept or reject.
 */
export async function amendAlgorithmTemplate(input: {
  manifest: Manifest;
  instruction: string;
  feedContext?: { name?: string; description?: string };
  useCustomNodes?: boolean;
}) {
  return await axios
    .post<FeedAmendment>(
      `${host}/api/v1/algorithm-templates/amend`,
      {
        manifest: input.manifest,
        instruction: input.instruction,
        feed_context: input.feedContext,
        use_custom_nodes: input.useCustomNodes ?? true,
      },
      requestSettings,
    )
    .then((res) => res.data);
}

export async function setMonetizationForAlgorithm(input: {
  algo_id: number;
  monetization: boolean;
}) {
  const urls = new URL(
    `${host}/api/v1/algorithm-management/set-monetization/${input.algo_id}/${input.monetization}`,
  );

  urls.searchParams.set(
    "monetization_available",
    input.monetization.toString(),
  );

  const res = await axios.get(urls.toString(), requestSettings);

  await queryClient.invalidateQueries({
    queryKey: ["my-feeds", input.algo_id],
  });

  return res.data as
    | {
        error: string;
      }
    | undefined;
}

export async function setContrailsForAlgorithm(input: {
  algo_id: number;
  contrails: boolean;
}) {
  const urls = new URL(
    `${host}/api/v1/algorithm-management/set-contrails/${input.algo_id}/${input.contrails}`,
  );

  urls.searchParams.set("contrails", input.contrails.toString());

  await axios.get(urls.toString(), requestSettings);

  await queryClient.invalidateQueries({
    queryKey: ["my-feeds", input.algo_id],
  });
}

export async function setCpmForAlgorithm(input: {
  algo_id: number;
  cpm_cents: number;
}) {
  await axios.get(
    `${host}/api/v1/algorithm-management/set-cpm-target/${input.algo_id}/${input.cpm_cents}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({
    queryKey: ["my-feeds", input.algo_id],
  });
}

export async function setSaturationLimitForAlgorithm(input: {
  algo_id: number;
  saturation_limit: number;
}) {
  await axios.get(
    `${host}/api/v1/algorithm-management/set-saturation-target/${input.algo_id}/${input.saturation_limit}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({
    queryKey: ["my-feeds", input.algo_id],
  });
}

async function getCampaigns() {
  const res = await axios
    .get<Campaign[]>(`${host}/api/v1/campaigns/`, requestSettings)
    .then((res) => res.data);

  return res.filter(Boolean);
}

export const campaignsQueryOptions = () =>
  queryOptions({
    queryKey: ["campaigns"],
    queryFn: () => getCampaigns(),
  });

async function getInboundCampaigns() {
  return await axios
    .get<InboundCampaign[]>(`${host}/api/v1/campaigns/inbound`, requestSettings)
    .then((res) => res.data);
}

export const inboundCampaignsQueryOptions = () =>
  queryOptions({
    queryKey: ["inbound-campaigns"],
    queryFn: () => getInboundCampaigns(),
  });

export interface CampaignNotification {
  text: string;
  type: "info" | "success" | "warning";
  category:
    | "incoming_proposed_campaign"
    | "outgoing_accepted_campaign"
    | "unread_message_thread";
  link: string;
}

async function getCampaignNotifications() {
  return await axios
    .get<{
      notifications: CampaignNotification[];
    }>(`${host}/api/v1/campaigns/notifications`, requestSettings)
    .then((res) => res.data.notifications);
}

export const campaignNotificationsQueryOptions = () =>
  queryOptions({
    queryKey: ["campaign-notifications"],
    queryFn: () => getCampaignNotifications(),
  });

interface CreateCampaignBaseInput {
  display_name: string;
}

interface CreateManualCampaignInput extends CreateCampaignBaseInput {
  type: "manual";
}

interface CreateProgrammaticCampaignInput extends CreateCampaignBaseInput {
  type: "auto";
  cpm_inflight: number;
  sticky_post_uris: string[];
}

type CreateCampaignInput =
  | CreateManualCampaignInput
  | CreateProgrammaticCampaignInput;

export async function createCampaign(input: CreateCampaignInput) {
  const res = await axios.post<Campaign>(
    `${host}/api/v1/campaigns/create`,
    { ...input, user_id: getUserId() },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["campaigns"] });
  return res.data;
}

async function getCampaign(campaignId: number) {
  return await axios
    .get<Campaign>(`${host}/api/v1/campaigns/${campaignId}`, requestSettings)
    .then((res) => res.data);
}

export const campaignQueryOptions = (campaignId: number) =>
  queryOptions({
    queryKey: ["campaign", campaignId],
    queryFn: () => getCampaign(campaignId),
  });

interface CampaignPost {
  post_url: string;
  is_active: boolean;
}

export async function addPostToCampaign(
  campaignId: number,
  post: CampaignPost,
) {
  const res = await axios.post<Post | { error: string }>(
    `${host}/api/v1/campaigns/${campaignId}/sticky-posts`,
    post,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });

  return res.data;
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface CreateProgrammaticAssetInput {
  text: string;
  link?: string | null;
  did?: string;
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface CreateProgrammaticAssetResponse {
  status: string;
  uri: string;
  cid: string;
  sticky_post_id: number;
  message?: string;
}

export async function createProgrammaticAsset(
  payload: CreateProgrammaticAssetInput,
) {
  if (!payload.link) {
    delete payload.link;
  }

  const res = await axios.post<CreateProgrammaticAssetResponse>(
    `${host}/api/v1/programmatic/assets`,
    payload,
    requestSettings,
  );

  await queryClient.invalidateQueries({ queryKey: ["programmatic-assets"] });

  return res.data;
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface DeleteProgrammaticAssetResponse {
  status: "deleted";
  asset_id: number;
  uri: string;
  message: string;
  warning?: string;
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export async function deleteProgrammaticAsset(assetId: number) {
  const res = await axios.delete<DeleteProgrammaticAssetResponse>(
    `${host}/api/v1/programmatic/assets/${assetId}`,
    requestSettings,
  );

  await queryClient.invalidateQueries({ queryKey: ["programmatic-assets"] });

  return res.data;
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface GetProgrammaticAssetsOptions {
  per_page?: number;
  campaign_id?: number;
}

async function getProgrammaticAssets(
  page: number,
  options: GetProgrammaticAssetsOptions = {},
) {
  const params = new URLSearchParams();

  params.set("page", page.toString());

  if (options.per_page !== undefined) {
    params.set("per_page", options.per_page.toString());
  }

  if (options.campaign_id !== undefined) {
    params.set("campaign_id", options.campaign_id.toString());
  }

  const url = `${host}/api/v1/programmatic/assets?${params.toString()}`;

  return await axios
    .get<ProgrammaticAssetsResponse>(url, requestSettings)
    .then((res) => res.data);
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export const programmaticAssetsInfiniteQueryOptions = (
  options: GetProgrammaticAssetsOptions = {},
) =>
  infiniteQueryOptions({
    queryKey: ["programmatic-assets", options],
    queryFn: ({ pageParam }) => getProgrammaticAssets(pageParam, options),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.pagination.page + 1;
      if (nextPage > lastPage.pagination.pages) {
        return undefined;
      }
      return nextPage;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
  });

export interface ProgrammaticRule {
  id: number;
  name: string;
  description: string;
  rule_type: "exclusion" | "inclusion";
  rule_text: string;
  threshold: number;
  weight: number;
  created_at?: string;
  updated_at?: string;
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface CreateProgrammaticRuleInput {
  name: string;
  description: string;
  rule_type: "exclusion" | "inclusion";
  rule_text: string;
  threshold: number;
  weight: number;
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface UpdateProgrammaticRuleInput {
  name?: string;
  description?: string;
  rule_type?: "exclusion" | "inclusion";
  rule_text?: string;
  threshold?: number;
  weight?: number;
}

export async function createProgrammaticRule(
  input: CreateProgrammaticRuleInput,
) {
  const res = await axios.post<ProgrammaticRule>(
    `${host}/rules`,
    input,
    requestSettings,
  );

  await queryClient.invalidateQueries({ queryKey: ["programmatic-rules"] });

  return res.data;
}

async function getProgrammaticRules() {
  return await axios
    .get<ProgrammaticRule[]>(`${host}/rules`, requestSettings)
    .then((res) => res.data);
}

export const programmaticRulesQueryOptions = () =>
  queryOptions({
    queryKey: ["programmatic-rules"],
    queryFn: () => getProgrammaticRules(),
  });

export async function updateProgrammaticRule(
  ruleId: number,
  input: UpdateProgrammaticRuleInput,
) {
  const res = await axios.put<ProgrammaticRule>(
    `${host}/rules/${ruleId}`,
    input,
    requestSettings,
  );

  await queryClient.invalidateQueries({ queryKey: ["programmatic-rules"] });

  return res.data;
}

export interface ExternalSSP {
  id: number;
  active: boolean;
  api_key: string;
  auth_token: string;
  bid_endpoint_url: string;
  contact_email: string;
  created_at: string;
  default_account_did: string;
  deleted_at: string | null;
  notes: string;
  partner_name: string;
  revenue_share: number;
  supported_native_formats: string[];
  timeout_ms: number;
  updated_at: string;
}

export interface AlgorithmProgrammaticSettings {
  enabled: boolean;
  cpm_floor_cents: number;
  daily_impression_cap: number;
  max_ad_percentage: number;
  rule_ids: number[];
  external_ssp_enabled: boolean;
  external_ssp_partner_ids?: number[];
  external_ssp_partners?: ExternalSSP[];
}

/** @public Work in progress: part of the programmatic-assets feature, written ahead of the UI that will consume it. Remove this tag when a caller lands, so knip guards it again. */
export interface UpdateAlgorithmProgrammaticSettingsInput {
  enabled?: boolean;
  cpm_floor_cents?: number;
  daily_impression_cap?: number;
  max_ad_percentage?: number;
  rule_ids?: number[];
  external_ssp_enabled?: boolean;
  external_ssp_partner_ids?: number[];
}

async function getAlgorithmProgrammaticSettings(algorithmId: number) {
  return await axios
    .get<AlgorithmProgrammaticSettings>(
      `${host}/algorithms/${algorithmId}/programmatic-settings`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const algorithmProgrammaticSettingsQueryOptions = (
  algorithmId: number,
) =>
  queryOptions({
    queryKey: ["algorithm-programmatic-settings?active_only=true", algorithmId],
    queryFn: () => getAlgorithmProgrammaticSettings(algorithmId),
  });

async function getExternalSSPs(): Promise<ExternalSSP[]> {
  return await axios
    .get<ExternalSSP[]>(`${host}/external-ssps`, requestSettings)
    .then((res) => res.data);
}

export const externalSSPsQueryOptions = () =>
  queryOptions<ExternalSSP[]>({
    queryKey: ["external-ssps"],
    queryFn: () => getExternalSSPs(),
  });

export async function updateAlgorithmProgrammaticSettings(
  algorithmId: number,
  input: UpdateAlgorithmProgrammaticSettingsInput,
) {
  const res = await axios.patch<AlgorithmProgrammaticSettings>(
    `${host}/algorithms/${algorithmId}/programmatic-settings`,
    input,
    requestSettings,
  );

  await queryClient.invalidateQueries({
    queryKey: ["algorithm-programmatic-settings?active_only=true", algorithmId],
  });
  await queryClient.invalidateQueries({ queryKey: ["my-feeds", algorithmId] });

  return res.data;
}

async function getCampaignsForAlgorithm(algo_id: number) {
  return await axios
    .get<InboundCampaign[]>(
      `${host}/api/v1/campaigns/algorithms/${algo_id}`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const campaignsForAlgorithmQueryOptions = (algo_id: number) =>
  queryOptions({
    queryKey: ["campaigns-for-algorithm", algo_id],
    queryFn: () => getCampaignsForAlgorithm(algo_id),
  });

interface CampaignProposal {
  cpm_cents: number;
  impressions_budget: number;
  reason?: string | undefined;
  coupon?: string | undefined;
}

export async function proposeCampaign(
  campaignId: number,
  algo_id: number,
  proposal: CampaignProposal,
) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/algorithms/${algo_id}/propose`,
    {
      ...proposal,
      coupon_code: proposal.coupon?.toUpperCase(),
    },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
}

export async function acceptCampaign(campaignId: number, algo_id: number) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/algorithms/${algo_id}/accept`,
    {},
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["campaigns-for-algorithm", algo_id],
    }),
    queryClient.invalidateQueries({ queryKey: ["inbound-campaigns"] }),
    queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
    queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] }),
  ]);
}

export async function rejectCampaign(
  campaignId: number,
  algo_id: number,
  reason: string,
) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/algorithms/${algo_id}/deny`,
    { reason },
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["campaigns-for-algorithm", algo_id],
    }),
    queryClient.invalidateQueries({ queryKey: ["inbound-campaigns"] }),
    queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
  ]);
}

export async function activateCampaign(campaignId: number, algo_id: number) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/algorithms/${algo_id}/activate`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
}

export async function deactivateCampaign(
  campaignId: number,
  algo_id: number,
  reason: string,
) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/algorithms/${algo_id}/deactivate`,
    { reason },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
}

export async function cancelCampaign(
  campaignId: number,
  algo_id: number,
  reason: string,
) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/algorithms/${algo_id}/cancel`,
    { reason },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
  await queryClient.invalidateQueries({ queryKey: ["inbound-campaigns"] });
}

export async function rescindCampaign(
  campaignId: number,
  algo_id: number,
  reason: string,
) {
  await axios.post(
    `${host}/api/v1/campaigns/${campaignId}/algorithms/${algo_id}/rescind`,
    { reason },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
}

export interface CreateOptions {
  email: string;
  business_type: "individual" | "company";
  company_name?: string;
  individual_first_name?: string;
  individual_last_name?: string;
  country: string;
}

export async function createStripeAccount(options: CreateOptions) {
  return await axios
    .post<{
      stripe_account_id: string;
      account_links: {
        url: string;
      };
    }>(
      `${host}/api/v1/payments/create-connected-account`,
      options,
      requestSettings,
    )
    .then((res) => res.data);
}

export async function addPaymentMethod(
  payment_method_id: string,
  email: string | undefined,
) {
  await axios
    .post<{
      checkout_session: {
        url: string;
      };
    }>(
      `${host}/api/v1/payments/add-payment-method`,
      email ? { payment_method_id, email } : { payment_method_id },
      requestSettings,
    )
    .then((res) => res.data);

  await queryClient.invalidateQueries({ queryKey: ["account-details"] });
}

async function getEarnedBalance() {
  return await axios
    .get<{
      total_payments: number;
      total_payments_pending: number;
      total_payouts: number;
      total_payouts_pending: number;
    }>(`${host}/api/v1/payments/balance`, requestSettings)
    .then((res) => res.data);
}

export const getEarnedBalanceQueryOptions = () =>
  queryOptions({
    queryKey: ["earned-balance"],
    queryFn: () => getEarnedBalance(),
  });

export async function nukeAccount() {
  await axios.get(`${host}/api/v1/payments/nuke-me`, requestSettings);
  await queryClient.invalidateQueries();
}

export async function setAccountAccountBio(bio: string) {
  await axios.post(
    `${host}/api/v1/payments/set-account-bio`,
    { bio },
    requestSettings,
  );
  await queryClient.invalidateQueries();
}

export async function successChargeAccount(session_id: string) {
  const url = new URL(`${host}/api/v1/payments/add-funds/success`);
  url.searchParams.set("session_id", session_id);
  await axios.get(url.toString(), requestSettings);
}

export interface AccountDetails {
  stripe_account_id: string;
  balance: number;
  withdrawable_balance: number;
  can_spend_on_campaigns: boolean;
  can_run_campaigns: boolean;
  ready_for_payment: boolean;
  payment_method_on_file?: {
    card?: {
      brand: string;
      last4: string;
    };
  };
  payout_method_on_file?: {
    bank_name: string;
    object: "bank_account";
    last4: string;
  } | null;
  stripe_payment_method_id: string;
  kyc_steps_remaining: string[];
  kyc_steps_remaining_link: {
    url?: string;
  };
}

async function getAccountDetails() {
  return await axios
    .get<AccountDetails>(
      `${host}/api/v1/payments/preflight-payment-payout-check`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getAccountDetailsQueryOptions = () =>
  queryOptions({
    queryKey: ["account-details"],
    queryFn: () => getAccountDetails(),
    retry: false,
  });

export async function favoriteCustomNode(nodeId: number) {
  await axios.post(
    `${host}/api/v1/algorithm-components/components/${nodeId}/favorite`,
    {},
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["custom-node", nodeId],
    }),
    queryClient.invalidateQueries({
      queryKey: ["search-custom-nodes"],
    }),
    queryClient.invalidateQueries({
      queryKey: ["user-custom-nodes"],
    }),
  ]);
}

export async function unfavoriteCustomNode(nodeId: number) {
  await axios.delete(
    `${host}/api/v1/algorithm-components/components/${nodeId}/favorite`,
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["custom-node", nodeId],
    }),
    queryClient.invalidateQueries({
      queryKey: ["search-custom-nodes"],
    }),
    queryClient.invalidateQueries({
      queryKey: ["user-custom-nodes"],
    }),
  ]);
}

export async function createTag(input: Tag) {
  await axios.post(`${host}/api/v1/tags`, input, requestSettings);
  await queryClient.invalidateQueries({ queryKey: ["tags"] });
}

export async function updateTag(tagId: number, input: Tag) {
  await axios.put(`${host}/api/v1/tags/${tagId}`, input, requestSettings);
  await queryClient.invalidateQueries({ queryKey: ["tags"] });
}

export async function deleteTag(tagId: number) {
  await axios.delete(`${host}/api/v1/tags/${tagId}`, requestSettings);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["tags"] }),
    queryClient.invalidateQueries({ queryKey: ["my-feeds"] }),
    queryClient.invalidateQueries({ queryKey: ["my-feeds-infinite"] }),
  ]);
}

async function listTags() {
  return await axios
    .get<SavedTag[]>(`${host}/api/v1/tags`, requestSettings)
    .then((res) =>
      res.data.sort((a, b) => a.display_name.localeCompare(b.display_name)),
    );
}

export const listTagsQueryOptions = () =>
  queryOptions({
    queryKey: ["tags"],
    queryFn: () => listTags(),
  });

export interface CreateTaggedItemOptions {
  tag_id: number;
  item_id: number;
  item_type: "algorithm" | "component";
}

export async function createTaggedItem(input: CreateTaggedItemOptions) {
  await axios.post(`${host}/api/v1/tags/items`, input, requestSettings);

  if (input.item_type === "component") {
    await queryClient.invalidateQueries({
      queryKey: ["custom-node", input.item_id],
    });
  } else {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["my-feeds-infinite"] }),
      queryClient.invalidateQueries({ queryKey: ["my-feeds", input.item_id] }),
    ]);
  }
}

export interface DeleteTaggedItemOptions {
  tag_id: number;
  item_id: number;
  item_type: "algorithm" | "component";
}

export async function deleteTaggedItem(input: DeleteTaggedItemOptions) {
  await axios.delete(
    `${host}/api/v1/tags/items/${input.item_type}/${input.item_id}/${input.tag_id}`,
    requestSettings,
  );

  if (input.item_type === "component") {
    await queryClient.invalidateQueries({
      queryKey: ["custom-node", input.item_id],
    });
  } else {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["my-feeds-infinite"] }),
      queryClient.invalidateQueries({ queryKey: ["my-feeds", input.item_id] }),
    ]);
  }
}

async function getTag(tagId: number) {
  return await axios
    .get<SavedTag>(`${host}/api/v1/tags/${tagId}`, requestSettings)
    .then((res) => res.data);
}

export const getTagQueryOptions = (tagId: number) =>
  queryOptions({
    queryKey: ["tag", tagId],
    queryFn: () => getTag(tagId),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export interface DocSearchResult {
  id: string;
  score: number;
  document: {
    type: "post" | "doc";
    url: string;
    content: string;
    title: string;
    description: string;
    tags: string[];
  };
}

export async function searchContent(query: string) {
  const baseSearchUrl =
    process.env.NODE_ENV === "production"
      ? `https://www.graze.social`
      : `http://localhost:4321`;

  return await axios
    .get<Array<DocSearchResult>>(
      `${baseSearchUrl}/search.json?query=${query}`,
      requestSettings,
    )
    .then((res) => res.data);
}

export async function getFeedRss(feedId: number) {
  return await axios
    .get<string>(`${host}/api/v1/rss/${feedId}/rss.xml`, requestSettings)
    .then((res) => res.data);
}

interface PublicFeedSitemapItem {
  id: number;
  uri: string;
  display_name: string;
  description: string;
}

export async function getPublicFeedsSitemap() {
  return await axios
    .get<Array<PublicFeedSitemapItem>>(
      `${host}/api/v1/sitemap/public_feeds`,
      requestSettings,
    )
    .then((res) => res.data);
}

export async function getMonetizedFeedsSitemap() {
  return await axios
    .get<Array<PublicFeedSitemapItem>>(
      `${host}/api/v1/sitemap/monetized_feeds`,
      requestSettings,
    )
    .then((res) => res.data);
}

export interface AppSetting {
  setting_name: string;
  setting_value: string | boolean | number | object;
}

export async function setUserSetting(options: AppSetting) {
  await axios.post(
    `${host}/api/v1/user_settings/set_setting`,
    options,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["current-user"] });
}

export async function setAlgoSetting(algo_id: number, options: AppSetting) {
  await axios.post(
    `${host}/api/v1/algo_settings/set_setting/${algo_id}`,
    options,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["my-feeds", algo_id] });
  if (options.setting_name === "feed_pulse_enabled") {
    await queryClient.invalidateQueries({ queryKey: ["feed-pulse", algo_id] });
    await queryClient.invalidateQueries({
      queryKey: ["feed-pulse-status", algo_id],
    });
  }
}

interface AuthorizationUrlResponse {
  authorization_url: string;
  linked?: boolean;
}

export async function linkPatreon() {
  const response = await axios
    .get<AuthorizationUrlResponse>(
      `${host}/api/v1/patreon/link_patreon`,
      requestSettings,
    )
    .then((res) => res.data);

  window.location.href = response.authorization_url;
}

export async function unlinkPatreon() {
  await axios.get(
    `${host}/api/v1/patreon/unlink_patreon_account`,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["patreon-account"] });
}

// Written by sync_account() in feed-processor on every 10-minute pass.
// "reauth_required" is the only one the creator can act on, and until
// 2026-09-08 nothing told them: four connections sat revoked for months.
// null means the account predates the field and has not synced since.
export type PatreonConnectionState =
  | "ok"
  | "reauth_required"
  | "no_campaign"
  | "error"
  | null;

async function getPatreonAccount() {
  return await axios
    .get<{
      account: {
        id: string;
        user_data: {
          attributes: {
            email: string;
            vanity: string;
            thumb_url: string;
            full_name: string;
          };
        };
        connection_state: PatreonConnectionState;
        last_sync_at: string | null;
        last_sync_error: string | null;
      };
    }>(`${host}/api/v1/patreon/get_patreon_account`, requestSettings)
    .then((res) => res.data);
}

export const getPatreonAccountQueryOptions = () =>
  queryOptions({
    queryKey: ["patreon-account"],
    queryFn: () => getPatreonAccount(),
  });

interface PatreonCampaign {
  id: number;
  campaign_id: string;
  creation_name: string;
  image_small_url: string;
  image_url: string;
  rss_feed_title: string;
}

async function getPatreonCampaigns() {
  return await axios
    .get<PatreonCampaign[]>(`${host}/api/v1/patreon/campaigns`, requestSettings)
    .then((res) => res.data);
}

export const getPatreonCampaignsQueryOptions = () =>
  queryOptions({
    queryKey: ["patreon-campaigns"],
    queryFn: () => getPatreonCampaigns(),
  });

interface PatreonTier {
  id: number;
  title: string;
  tier_id: string;
}

async function getPatreonCampaginTeirs(campaignId?: string) {
  if (!campaignId) {
    return [];
  }

  return await axios
    .get<Array<PatreonTier>>(
      `${host}/api/v1/patreon/campaigns/${campaignId}/tiers`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getPatreonCampaginTeirsQueryOptions = (campaignId?: string) =>
  queryOptions({
    queryKey: ["patreon-campaign-teirs", campaignId],
    queryFn: () => getPatreonCampaginTeirs(campaignId),
  });

export async function resyncPatreonCampaigns() {
  await axios.get(`${host}/api/v1/patreon/resync`, requestSettings);
  await queryClient.invalidateQueries({ queryKey: ["patreon-campaigns"] });
}

export async function handlePatreonCallback(code: string) {
  await axios.get(
    `${host}/api/v1/patreon/callback?code=${code}`,
    requestSettings,
  );
}

export interface PatreonCapability {
  id: number;
  display_name: string;
  capability: string;
}

async function getPatreonCapabilities() {
  return await axios
    .get<Array<PatreonCapability>>(
      `${host}/api/v1/patreon/capabilities`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getPatreonCapabilitiesQueryOptions = () =>
  queryOptions({
    queryKey: ["patreon-capabilities"],
    queryFn: () => getPatreonCapabilities(),
  });

interface AddCapabilityToFeedInputBase {
  algo_id: number;
  capability_ids: number[];
}

interface AddMemberCapabilityToFeedInput extends AddCapabilityToFeedInputBase {
  membership_type: "member";
  tier_id: number;
}

interface AddNonMemberCapabilityToFeedInput
  extends AddCapabilityToFeedInputBase {
  membership_type: "anon" | "user";
}

export type AddCapabilityToFeedInput =
  | AddMemberCapabilityToFeedInput
  | AddNonMemberCapabilityToFeedInput;

export async function addCapabilityToFeed(input: AddCapabilityToFeedInput) {
  await axios.post(
    `${host}/api/v1/patreon/capabilities`,
    input,
    requestSettings,
  );
  await queryClient.invalidateQueries({
    queryKey: ["patreon-capabilities-for-feed", input.algo_id],
  });
}

export async function deleteCapabilityFromFeed(
  input: AddCapabilityToFeedInput,
) {
  await axios.delete(`${host}/api/v1/patreon/capabilities`, {
    ...requestSettings,
    data: input,
  });
  await queryClient.invalidateQueries({
    queryKey: ["patreon-capabilities-for-feed", input.algo_id],
  });
}

async function getCapabilitiesForFeed(algo_id: number) {
  return await axios
    .get<
      Array<
        | {
            tier_id: number;
            tier: {
              title: string;
              patreon_campaign: {
                rss_feed_title: string;
              };
            };
            membership_type: "member";
            algorithm_id: number;
            reader_capability_id: number;
          }
        | {
            tier_id: null;
            membership_type: "user" | "member";
            algorithm_id: number;
            reader_capability_id: number;
          }
      >
    >(`${host}/api/v1/patreon/algos/${algo_id}/capabilities`, requestSettings)
    .then((res) => res.data);
}

export const getCapabilitiesForFeedQueryOptions = (algo_id: number) =>
  queryOptions({
    queryKey: ["patreon-capabilities-for-feed", algo_id],
    queryFn: () => getCapabilitiesForFeed(algo_id),
  });

export async function startReaderPatreonFlow() {
  const response = await axios.get<AuthorizationUrlResponse>(
    `${host}/api/v1/patreon/start_connect_patreon`,
    requestSettings,
  );

  window.location.href = response.data.authorization_url;
}

export async function handleReaderPatreonCallback(code: string) {
  const response = await axios.get<AuthorizationUrlResponse>(
    `${host}/api/v1/patreon/connect_patreon_callback?code=${code}`,
    requestSettings,
  );
  return response.data;
}

async function getRelevantAutoMigrateFeeds() {
  return await axios
    .get<Array<ExternalFeed>>(
      `${host}/api/v1/auto-migrate/relevant-feeds`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getRelevantAutoMigrateFeedsQueryOptions = () =>
  queryOptions({
    queryKey: ["patreon-capabilities"],
    queryFn: () => getRelevantAutoMigrateFeeds(),
  });

async function getManifestForAutoMigrateFeed(feedUri: string) {
  const response = await axios.post<{ manifest: Manifest }>(
    `${host}/api/v1/auto-migrate/get-manifest`,
    {
      feed_uri: feedUri,
    },
    {
      ...requestSettings,
    },
  );

  return response.data;
}

export const getManifestForAutoMigrateFeedQueryOptions = (feedUri: string) =>
  queryOptions({
    queryKey: ["auto-migrate-manifest", feedUri],
    queryFn: () => getManifestForAutoMigrateFeed(feedUri),
  });

export async function createLinkTrackedPost(input: {
  text: string;
  link?: string;
  include_tracking?: boolean;
}) {
  return await axios.post<{
    uri: string;
    cid: string;
    short_url: string;
  }>(`${host}/api/v1/post-composer/create`, input, requestSettings);
}

export async function deleteAccountData(input: { email: string }) {
  await axios.post(`${host}/api/v1/privacy-compliance/request-removal`, input, {
    ...requestSettings,
  });
}

async function getDataRemovalStats(year?: number) {
  const response = await axios.post<{
    total_requests: number;
    complied_requests: number;
    denied_requests: number;
    median_response_days: number;
    mean_response_days: number;
    include_deleted: boolean;
    year: number | null;
  }>(
    `${host}/api/v1/privacy-compliance/request-stats`,
    {},
    // requestSettings carries no params of its own, so this sets rather than merges.
    // Spreading `requestSettings.params` here would be an `any` spread and fails lint.
    year === undefined ? requestSettings : { ...requestSettings, params: { year } },
  );

  return response.data;
}

// `year` is required for anything rendered under a year label. The endpoint
// returns LIFETIME totals when it is omitted, and the CCPA Metrics Disclosure
// Chart reports per calendar year -- omitting it published 761 lifetime
// requests as "received in 2025" when 2025's figure was 96.
export const getDataRemovalStatsQueryOptions = (year?: number) =>
  queryOptions({
    queryKey: ["data-removal-stats", year ?? "lifetime"],
    queryFn: () => getDataRemovalStats(year),
  });

interface Session {
  id: number;
  created_at: string;
  last_used_at: string;
  expires_at: string;
  user_agent: string;
  ip_address: string;
  device_name: string | null;
  is_current: boolean;
  session_type: "password";
}

async function getSessions() {
  return await axios
    .get<{ sessions: Array<Session> }>(
      `${host}/api/v1/user-sessions/`,
      requestSettings,
    )
    .then((res) => res.data.sessions);
}

export const getSessionsQueryOptions = () =>
  queryOptions({
    queryKey: ["sessions"],
    queryFn: () => getSessions(),
  });

export async function revokeSession(sessionId: number) {
  await axios.delete(
    `${host}/api/v1/user-sessions/${sessionId}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["sessions"] });
}

export async function revokeAllSessions() {
  await axios.post(
    `${host}/api/v1/user-sessions/revoke-all`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["sessions"] });
}

export async function logEmail(email: string) {
  await axios.post(
    `${host}/log-publisher-request?email=${email}`,
    {},
    requestSettings,
  );
}

interface MessageCampaignContext {
  algo_id: number;
  campaign_id: number;
  type: "campaign";
}

export type MessageContext = MessageCampaignContext;

async function sendMessage(options: {
  recipient_id: number;
  subject: string;
  body: string;
  context: MessageContext;
}) {
  return await axios
    .post<SendMessageResponse>(
      `${host}/api/v1/messages/messages/`,
      options,
      requestSettings,
    )
    .then((r) => r.data);
}

export interface SendMessageResponse {
  id: number;
  sender_id: number;
  recipient_id: number;
  subject: string;
  body: string;
  context: MessageContext;
  created_at: string;
  recipient_read_at: string | null;
}

async function getInbox(options: {
  context: MessageContext;
  skip?: number;
  limit?: number;
}) {
  const url = new URL(`${host}/api/v1/messages/messages/thread`);

  url.searchParams.set(
    "context",
    encodeURIComponent(JSON.stringify(options.context)),
  );
  url.searchParams.set("skip", options.skip?.toString() ?? "0");
  url.searchParams.set("limit", options.limit?.toString() ?? "50");

  return await axios
    .get<Array<SendMessageResponse>>(url.toString(), {
      ...requestSettings,
    })
    .then((r) => r.data);
}

let sendMessageTimeout: NodeJS.Timeout | null = null;

export async function sendCampaginMessage(options: {
  recipient_id: number;
  body: string;
  context: {
    algo_id: number;
    campaign_id: number;
  };
}) {
  const message = await sendMessage({
    ...options,
    subject: "Campaign Message",
    context: {
      ...options.context,
      type: "campaign",
    },
  });
  await queryClient.invalidateQueries({
    queryKey: ["inbox", options.context.campaign_id, options.context.algo_id],
  });

  sendMessageTimeout = setTimeout(() => {
    if (sendMessageTimeout) {
      clearTimeout(sendMessageTimeout);
    }

    const messageEl = document.querySelector(`#message-${message.id}`);

    if (messageEl) {
      messageEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }, 100);
}

async function getInboxForFeed(options: {
  campaignId: number;
  feedId: number;
  skip: number;
  limit: number;
}) {
  return {
    data: await getInbox({
      skip: options.skip,
      limit: options.limit,
      context: {
        campaign_id: options.campaignId,
        algo_id: options.feedId,
        type: "campaign",
      },
    }),
    cursor: options.skip + options.limit,
  };
}

export const getInboxForFeedQueryOptions = (options: {
  campaignId: number;
  feedId: number;
  skip?: number;
  limit?: number;
}) =>
  infiniteQueryOptions({
    queryKey: ["inbox", options.campaignId, options.feedId],
    queryFn: ({ pageParam }) =>
      getInboxForFeed({
        ...options,
        skip: pageParam?.skip ?? 0,
        limit: options?.limit ?? 50,
      }),
    getNextPageParam: (lastPage) =>
      lastPage?.data?.length > 0
        ? {
            skip: lastPage.cursor,
          }
        : null,
    initialPageParam: {
      skip: options.skip,
    },
    refetchInterval: 2000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

export async function markMessageAsRead(
  messageId: number,
  context: MessageContext,
) {
  await axios.get(`${host}/api/v1/messages/messages/messages/${messageId}`, {
    ...requestSettings,
  });
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["campaign", context.campaign_id],
    }),
  ]);
}

async function getUnreadCountForInbox(options: { context: MessageContext }) {
  const url = new URL(`${host}/api/v1/messages/messages/unread_count`);

  url.searchParams.set(
    "context",
    encodeURIComponent(JSON.stringify(options.context)),
  );

  return await axios
    .get<{ unread_count: number }>(url.toString(), {
      ...requestSettings,
    })
    .then((r) => r.data.unread_count);
}

export const getUnreadCountForInboxQueryOptions = (options: {
  context: MessageContext;
}) =>
  queryOptions({
    queryKey: [
      "inbox",
      "unread_count",
      options.context.campaign_id,
      options.context.algo_id,
    ],
    queryFn: () => getUnreadCountForInbox(options),
  });
export interface DeprecationCheck {
  algorithm_id: number;
  algorithm_uri: string;
  text_arbitrary: boolean;
  image_arbitrary: boolean;
  deprecated: boolean;
  deprecated_conditions: Array<{
    operator: string;
    parameter_value: string;
    reason: string;
  }>;
}

async function getUserDeprecationCheck() {
  return await axios
    .get<{
      user_id: number;
      algorithms: Record<string, DeprecationCheck>;
    }>(`${host}/api/v1/algo_settings/deprecation-check`, requestSettings)
    .then((res) => res.data);
}

export const getUserDeprecationCheckQueryOptions = () =>
  queryOptions({
    queryKey: ["user-deprecation-check"],
    queryFn: () => getUserDeprecationCheck(),
  });

export async function getGlobalStats() {
  return await axios
    .get<{
      unique_users: number;
      unique_delivers: number;
    }>(`${host}/api/v1/global-stats/stats`, requestSettings)
    .then((res) => res.data);
}

export async function createCoupon({
  algorithm_id,
  ...options
}: {
  algorithm_id: number;
  custom_code: string;
  name: string;
  discount_percentage: number;
  max_uses?: number;
  expires_in_days?: number;
}) {
  await axios.post(
    `${host}/api/v1/coupons/algorithms/${algorithm_id}/coupons`,
    options,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["coupons", algorithm_id] });
}

export interface Coupon {
  code: string;
  created_at: string;
  discount_percentage: number;
  expires_at: string | null;
  id: number;
  is_active: boolean;
  max_uses: number | null;
  name: string;
  uses_count: number;
}

async function getCoupons(algorithm_id: number) {
  return await axios
    .get<Coupon[]>(
      `${host}/api/v1/coupons/algorithms/${algorithm_id}/coupons`,
      requestSettings,
    )
    .then((res) => res.data);
}

export const getCouponsQueryOptions = (algorithm_id: number) =>
  queryOptions({
    queryKey: ["coupons", algorithm_id],
    queryFn: () => getCoupons(algorithm_id),
  });

export async function deleteCoupon(coupon_id: number, algorithm_id: number) {
  await axios.delete(
    `${host}/api/v1/coupons/coupons/${coupon_id}`,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["coupons", algorithm_id] });
}

type ValidateCouponResponse =
  | {
      error: string;
      valid: false;
    }
  | {
      amount_saved_cents: number;
      discount_percentage: number;
      discounted_cpm_cents: number;
      message: string;
      original_cpm_cents: number;
      valid: true;
    };

export async function validateCoupon({
  coupon_code,
  algorithm_id,
  cpm_cents,
}: {
  coupon_code: string;
  algorithm_id: number;
  cpm_cents: number;
}) {
  const url = new URL(`${host}/api/v1/coupons/validate-coupon`);

  url.searchParams.set("coupon_code", coupon_code.toUpperCase());
  url.searchParams.set("algorithm_id", algorithm_id.toString());
  url.searchParams.set("cpm_cents", cpm_cents.toString());

  return await axios
    .get<ValidateCouponResponse>(url.toString(), requestSettings)
    .then((res) => res.data);
}

// ---------------------------------------------------------------------------
// Feed Gym v2's client used to sit here: eleven read endpoints, seven response types and nine
// query options, serving a creator page of epochs, duels, events, findings and proposals.
//
// It went when v2 did. The page it fed was replaced by two v3 routes — one fleet-wide results
// page and one per-feed page — and its client is further down beside them. Nothing imported any
// of it any more, which is how `knip` came to be failing `pnpm lint` on main.
//
// 🔴 `gymAvailabilityQueryOptions` WENT WITH IT, AND THAT ONE WAS STILL BEING CALLED. It asked
// `/api/v1/feed_gym/availability` whether this user was on v2's burn-in allowlist, and the canvas
// editor hid its gym tool unless the answer was yes. Removing v2's router from `api` took the
// endpoint away; the auth middleware answers 401 before routing, so the query failed closed and
// the tool silently disappeared for everyone. v3 has no allowlist — enrolment is a layer any
// creator can add — so the gate is gone rather than repointed, and the tool always shows.
// ---------------------------------------------------------------------------

/* ------------------------------------------------------------------ *
 * Sticky Sets
 *
 * One named set of sticky posts, applied to many feeds at once. Editing a
 * post in a set edits it everywhere the set reaches; the per-feed panel can
 * still override or detach an individual copy.
 * ------------------------------------------------------------------ */

export interface StickySetSummary {
  id: number;
  display_name: string;
  description: string | null;
  scope_mode: "explicit" | "tag";
  scope_tag_id: number | null;
  is_active: boolean;
  order_offset: number;
  member_count: number;
  feed_count: number;
  /** How many feed attachments actually exist. 0 means it has never run. */
  materialised_rows: number;
  created_at: string;
  updated_at: string;
}

export interface StickySetMember {
  id: number;
  sticky_post_id: number;
  uri: string | null;
  author: string | null;
  sticky_type: "pinned" | "injected";
  stopping_criteria?: { expiration_time?: number } | null;
  order: number | null;
  is_active: boolean;
  created_at: string;
}

export interface StickySetDetail extends StickySetSummary {
  members: StickySetMember[];
  feeds: { id: number; display_name: string; record_name: string }[];
}

interface ReconcileCounts {
  feeds: number;
  members: number;
  desired: number;
  insert: number;
  update: number;
  delete: number;
  release: number;
  opted_out: number;
}

interface StickySetDrift {
  sticky_set_id: number;
  counts: ReconcileCounts;
  /** Feeds that are missing posts the set says they should have. */
  feeds_missing_posts: { algorithm_id: number; missing: number }[];
  feeds_complete: number[];
  opted_out_rows: number[];
}

interface StickySetSuggestion {
  sticky_post_id: number;
  uri: string;
  sticky_type: "pinned" | "injected";
  feed_count: number;
}

/** A set-managed sticky post row on one particular feed. */
export interface ManagedStickyRow {
  association_id: number;
  sticky_post_id: number;
  sticky_set_id: number;
  sticky_set_name: string;
  sticky_type: "pinned" | "injected";
  is_active: boolean;
  opted_out: boolean;
  adopted: boolean;
}

const stickySetsHost = `${host}/api/v1/sticky-sets`;

async function getStickySets() {
  return axios
    .get<{ sticky_sets: StickySetSummary[] }>(stickySetsHost, requestSettings)
    .then((res) => res.data.sticky_sets);
}

export const getStickySetsQueryOptions = () =>
  queryOptions({
    queryKey: ["sticky-sets"],
    queryFn: () => getStickySets(),
  });

async function getStickySet(setId: number) {
  return axios
    .get<StickySetDetail>(`${stickySetsHost}/${setId}`, requestSettings)
    .then((res) => res.data);
}

export const getStickySetQueryOptions = (setId: number) =>
  queryOptions({
    queryKey: ["sticky-set", setId],
    queryFn: () => getStickySet(setId),
  });

async function getStickySetDrift(setId: number) {
  return axios
    .get<StickySetDrift>(`${stickySetsHost}/${setId}/drift`, requestSettings)
    .then((res) => res.data);
}

export const stickySetDriftQueryOptions = (setId: number) =>
  queryOptions({
    queryKey: ["sticky-set", setId, "drift"],
    queryFn: () => getStickySetDrift(setId),
  });

async function getStickySetSuggestions(minFeeds = 20) {
  const url = new URL(`${stickySetsHost}/suggestions`);
  url.searchParams.set("min_feeds", `${minFeeds}`);
  return axios
    .get<{ min_feeds: number; suggestions: StickySetSuggestion[] }>(
      url.toString(),
      requestSettings,
    )
    .then((res) => res.data.suggestions);
}

export const stickySetSuggestionsQueryOptions = (minFeeds = 20) =>
  queryOptions({
    queryKey: ["sticky-set-suggestions", minFeeds],
    queryFn: () => getStickySetSuggestions(minFeeds),
  });

async function getStickySetsForFeed(feedId: number) {
  return axios
    .get<{ algorithm_id: number; managed_rows: ManagedStickyRow[] }>(
      `${stickySetsHost}/by-feed/${feedId}`,
      requestSettings,
    )
    .then((res) => res.data.managed_rows);
}

export const stickySetsForFeedQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["sticky-sets-for-feed", feedId],
    queryFn: () => getStickySetsForFeed(feedId),
  });

/**
 * Anything that changes a set changes the sticky posts on every feed it
 * reaches, so the feed-level caches have to go too -- otherwise a feed page
 * shows a stale list until it happens to refetch.
 */
async function invalidateStickySet(setId?: number) {
  await queryClient.invalidateQueries({ queryKey: ["sticky-sets"] });
  if (setId !== undefined) {
    await queryClient.invalidateQueries({ queryKey: ["sticky-set", setId] });
  }
  void queryClient.invalidateQueries({ queryKey: ["sticky-posts"] });
  void queryClient.invalidateQueries({ queryKey: ["sticky-posts-infinite"] });
  void queryClient.invalidateQueries({ queryKey: ["sticky-sets-for-feed"] });
  void queryClient.invalidateQueries({ queryKey: ["feed-preview"] });
}

export async function createStickySet(payload: {
  display_name: string;
  description?: string;
  scope_mode?: "explicit" | "tag";
  scope_tag_id?: number;
  order_offset?: number;
  algorithm_ids?: number[];
}) {
  const res = await axios.post<StickySetSummary>(
    stickySetsHost,
    payload,
    requestSettings,
  );
  await invalidateStickySet();
  return res.data;
}

export async function updateStickySet(
  setId: number,
  payload: {
    display_name?: string;
    description?: string;
    scope_mode?: "explicit" | "tag";
    scope_tag_id?: number;
    is_active?: boolean;
    order_offset?: number;
  },
) {
  const res = await axios.put<StickySetSummary & { reconcile: ReconcileCounts }>(
    `${stickySetsHost}/${setId}`,
    payload,
    requestSettings,
  );
  await invalidateStickySet(setId);
  return res.data;
}

export async function deleteStickySet(setId: number) {
  await axios.delete(`${stickySetsHost}/${setId}`, requestSettings);
  await invalidateStickySet(setId);
}

export async function addPostToStickySet(
  setId: number,
  payload: {
    post_url: string;
    sticky_type: "pinned" | "injected";
    expiration?: Date;
    order?: number;
  },
) {
  const res = await axios.post<{
    member: StickySetMember;
    reconcile: ReconcileCounts;
  }>(
    `${stickySetsHost}/${setId}/posts`,
    {
      post_url: payload.post_url,
      sticky_type: payload.sticky_type,
      order: payload.order,
      stopping_criteria: payload.expiration
        ? { expiration_time: Math.floor(payload.expiration.getTime() / 1000) }
        : undefined,
    },
    requestSettings,
  );
  await invalidateStickySet(setId);
  return res.data;
}

export async function editPostInStickySet(
  setId: number,
  memberId: number,
  payload: {
    sticky_type?: "pinned" | "injected";
    expiration?: Date | null;
    order?: number;
    is_active?: boolean;
  },
) {
  const res = await axios.put<{
    member: StickySetMember;
    reconcile: ReconcileCounts;
  }>(
    `${stickySetsHost}/${setId}/posts/${memberId}`,
    {
      sticky_type: payload.sticky_type,
      order: payload.order,
      is_active: payload.is_active,
      stopping_criteria:
        payload.expiration === null
          ? {}
          : payload.expiration
            ? {
                expiration_time: Math.floor(
                  payload.expiration.getTime() / 1000,
                ),
              }
            : undefined,
    },
    requestSettings,
  );
  await invalidateStickySet(setId);
  return res.data;
}

export async function removePostFromStickySet(
  setId: number,
  memberId: number,
) {
  await axios.delete(
    `${stickySetsHost}/${setId}/posts/${memberId}`,
    requestSettings,
  );
  await invalidateStickySet(setId);
}

export async function addFeedsToStickySet(setId: number, feedIds: number[]) {
  const res = await axios.post<{
    added: number[];
    reconcile: ReconcileCounts;
  }>(
    `${stickySetsHost}/${setId}/feeds`,
    { algorithm_ids: feedIds },
    requestSettings,
  );
  await invalidateStickySet(setId);
  return res.data;
}

export async function removeFeedFromStickySet(setId: number, feedId: number) {
  await axios.delete(
    `${stickySetsHost}/${setId}/feeds/${feedId}`,
    requestSettings,
  );
  await invalidateStickySet(setId);
}

export async function reconcileStickySet(setId: number) {
  const res = await axios.post<{
    dry_run: boolean;
    counts: ReconcileCounts;
    inserted: number;
    adopted: number;
    feeds_touched: number;
  }>(`${stickySetsHost}/${setId}/reconcile`, {}, requestSettings);
  await invalidateStickySet(setId);
  return res.data;
}

/** Build a set out of an owner's existing hand-made fan-out. */
export async function adoptStickySet(payload: {
  display_name: string;
  min_feeds?: number;
  description?: string;
}) {
  const res = await axios.post<
    StickySetSummary & {
      members_added: number;
      feeds_added: number;
      adopted_rows: number;
      drift_closed: number;
    }
  >(`${stickySetsHost}/adopt`, payload, requestSettings);
  await invalidateStickySet();
  void queryClient.invalidateQueries({ queryKey: ["sticky-set-suggestions"] });
  return res.data;
}

/** Make one feed's copy local, so this feed can edit it freely. */
export async function detachStickyRow(associationId: number) {
  await axios.post(
    `${stickySetsHost}/rows/${associationId}/detach`,
    {},
    requestSettings,
  );
  await invalidateStickySet();
}

/** Turn a set-managed post off (or back on) for one feed only. */
export async function setStickyRowOptOut(
  associationId: number,
  optedOut: boolean,
) {
  const url = new URL(`${stickySetsHost}/rows/${associationId}/opt-out`);
  url.searchParams.set("opted_out", `${optedOut}`);
  await axios.post(url.toString(), {}, requestSettings);
  await invalidateStickySet();
}

// --- Rate limits -------------------------------------------------------------
//
// `text_predicate` (the canvas's "Natural Language" node) scores every candidate post on
// a GPU, so it is metered per feed per hour. When a feed runs out, the node answers with
// whatever the author chose for it and the feed carries on quietly — which is exactly why
// this has to be readable after the fact rather than only while it is happening.

const rateLimitsHost = `${host}/api/v1/rate-limits`;

async function getFeedRateLimits(algo_id: number, hours: number) {
  return await axios
    .get<FeedRateLimits>(`${rateLimitsHost}/feed/${algo_id}?hours=${hours}`, requestSettings)
    .then((res) => res.data);
}

export const feedRateLimitsQueryOptions = (algo_id: number, hours = 72) =>
  queryOptions({
    queryKey: ["rate-limits", algo_id, hours],
    queryFn: () => getFeedRateLimits(algo_id, hours),
    // The matcher flushes its counters every ten seconds and the page shows an hour at a
    // time, so anything fresher than a minute is churn for no new information.
    staleTime: 60_000,
  });

async function getRecentRateLimits(days: number) {
  return await axios
    .get<RecentRateLimits>(`${rateLimitsHost}/recent?days=${days}`, requestSettings)
    .then((res) => res.data);
}

/**
 * Which of this user's feeds were throttled recently — the "while you were away" query.
 * Long stale time: it backs a banner, and a banner that refetches on every focus change
 * is a banner that flickers.
 */
export const recentRateLimitsQueryOptions = (days = 7) =>
  queryOptions({
    queryKey: ["rate-limits-recent", days],
    queryFn: () => getRecentRateLimits(days),
    staleTime: 5 * 60_000,
  });

/**
 * Authorize a feed to spend more than the default. Admin-only server-side; the page only
 * offers it when the server says `can_grant`.
 */
export async function createQuotaGrant(
  algo_id: number,
  body: {
    /**
     * 🔴 REQUIRED, and the reason this parameter is not optional.
     *
     * The server defaults an absent `resource` to `adjudication`, and this call used not to
     * send one — so authorizing a higher limit for a feed metered on `text_question` wrote a
     * valid grant against a budget that feed never spends. It published, it showed as "in
     * force", and the feed carried on being throttled. Pass the resource of the block the
     * button was rendered under, never a constant.
     */
    resource: string;
    units: number;
    window_secs: number;
    reason: string;
    expires_at?: string;
  },
) {
  const result = await axios
    .post<QuotaGrant>(`${rateLimitsHost}/feed/${algo_id}/grants`, body, requestSettings)
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["rate-limits", algo_id] });
  return result;
}

/**
 * Ask for a bigger budget for this feed.
 *
 * The author path, not the admin one: it authorizes the feed, and it changes nothing on
 * its own. Before this existed the only way to ask was out of band, which in practice
 * meant whoever already knew someone got an exception.
 */
export async function requestQuotaIncrease(
  algo_id: number,
  /** `resource` required for the same reason as `createQuotaGrant` — a request filed against
   *  the wrong budget carries the wrong budget's usage as its evidence. */
  body: { resource: string; reason: string; requested_units?: number },
) {
  const result = await axios
    .post<QuotaIncreaseRequest>(
      `${rateLimitsHost}/feed/${algo_id}/requests`,
      body,
      requestSettings,
    )
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["rate-limits", algo_id] });
  return result;
}

/** What `DELETE /feed/{id}/requests/{id}` actually answers with.
 *
 * NOT a `QuotaIncreaseRequest`, despite the two calls either side of this one returning
 * their record -- the route returns a small acknowledgement (`app/api/rate_limits.py`
 * `withdraw_request`). Typing it like its neighbours would have been a lie that compiled.
 */
interface QuotaWithdrawAck {
  ok: boolean;
  id: number;
  status: string;
}

/** Withdraw a request. Soft: the record of having asked survives. */
export async function withdrawQuotaIncrease(algo_id: number, requestId: number) {
  const result = await axios
    .delete<QuotaWithdrawAck>(
      `${rateLimitsHost}/feed/${algo_id}/requests/${requestId}`,
      requestSettings,
    )
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["rate-limits", algo_id] });
  return result;
}

/** Take a carve-out away. The row survives; only its effect ends. */
export async function revokeQuotaGrant(algo_id: number, grantId: number) {
  const result = await axios
    .post<QuotaGrant>(`${rateLimitsHost}/grants/${grantId}/revoke`, {}, requestSettings)
    .then((res) => res.data);
  await queryClient.invalidateQueries({ queryKey: ["rate-limits", algo_id] });
  return result;
}

// ---------------------------------------------------------------------------
// Feed Gym v3
//
// Three routes replacing eleven. The previous surface rendered eleven slices of internal
// machinery — arms, epochs, duels, allocations, events, proposals — and the page built from them
// was unreadable to the person who wrote it. What a creator needs is four things; what the public
// needs is the research.
//
// 🔴 NOTHING HERE IS AN INTERNAL IDENTIFIER. No arm ids, no epochs, no team markers, no design
// JSON. An experiment is a NAME and a RESULT. The one place the old page leaked internals was
// `GymArmRef.description`, which was `feed_gym_policies.notes` — one column doing two jobs, a
// short label for most arms and 805 characters of design rationale for one, all of it rendered.

/** One experiment, as any page should see it. */
export interface GymExperimentView {
  id: number;
  /** Plain English, derived from the design and frozen when it opened. */
  name: string;
  status: string;
  /** The status in a creator's words, so no page has to own that mapping. */
  outcome: string;
  started: string | null;
  finished: string | null;
  /** Why the search chose this question. Written from the features that carried it. */
  reason: string | null;
  decisive: number | null;
  wins: number | null;
  winRate: number | null;
  low: number | null;
  high: number | null;
  feeds: number | null;
  /** One sentence stating what happened, written by the judge. */
  summary: string | null;
  /**
   * Positive AND past the minimum effect worth acting on.
   *
   * A bare positive that squeaks over the evidence bar is a real result and a poor
   * recommendation, so only a proven one is offered to a creator.
   */
  proven: boolean;
}

interface GymWeightEvent {
  event: string;
  label: string;
  weight: number;
  /** Said plainly rather than left for a reader to infer from a zero. */
  note: string | null;
}

interface GymPublic {
  now: GymExperimentView | null;
  next: GymExperimentView | null;
  proven: GymExperimentView[];
  experiments: GymExperimentView[];
  enrolledFeeds: number;
  /**
   * The A/A check as the GYM computed it — pooled across experiments since the mechanism last
   * changed, with its own measured null, its state and its sentence.
   *
   * 🔴 NOT RECOMPUTED HERE, AND NOT A STYLE CHOICE. The API used to count these sessions itself,
   * scoped to the running experiment, which reset the figure to zero at every rollover — 18 of 200
   * on the page while the real pooled figure was 49 of 89, against a bar the partitioned count
   * could never reach. The pooling window is a constant that lives in the gym; every copy of it
   * elsewhere is a place to forget.
   */
  instrument: {
    decisive: number;
    wins: number;
    /** The candidate's share of the A/A's own impressions. Not assumed to be a half. */
    null: number | null;
    /** `even` | `biased` | `not_yet` — and the third is not a failure. */
    state: string | null;
    /** How many decisive visits certification needs. */
    minimum: number | null;
    /** The gym's own wording for `state`. Rendered as-is; see the page. */
    sentence: string | null;
    since: string | null;
  } | null;
  weights: {
    version: number;
    windowDays: number;
    computedAt: string;
    events: GymWeightEvent[];
  } | null;
}

/** One experiment with this feed's own contribution to it. */
interface GymFeedExperiment extends GymExperimentView {
  yourSessions: number;
  yourDecisive: number;
  yourWins: number;
}

interface GymFeedPage {
  feed: { id: number; name: string };
  /** Null when the feed has no gym layer. The layer IS the opt-in. */
  layer: { share: number | null; paused: boolean } | null;
  now: GymFeedExperiment | null;
  chart: { day: string; control: number; candidate: number }[];
  history: GymFeedExperiment[];
  suggestions: GymExperimentView[];
}

async function getGymPublic(): Promise<GymPublic> {
  // 🔴 NO CREDENTIALS. This route is in the auth middleware's exempt list, and sending the
  // session cookie to it would make a page that works when logged in and 401s when not — which is
  // exactly the failure a public results page must not have.
  const response = await axios.get<GymPublic>(`${host}/api/v1/gym/public`);
  return response.data;
}

async function getGymFeed(feedId: number): Promise<GymFeedPage> {
  const response = await axios.get<GymFeedPage>(
    `${host}/api/v1/gym/feeds/${feedId}`,
    requestSettings,
  );
  return response.data;
}

export async function adoptGymLayer(feedId: number, experimentId: number) {
  const response = await axios.post<{ ok: boolean; manifestId: number; name: string }>(
    `${host}/api/v1/gym/feeds/${feedId}/adopt/${experimentId}`,
    {},
    requestSettings,
  );
  return response.data;
}

export const gymPublicQueryOptions = () =>
  queryOptions({
    queryKey: ["gym", "public"],
    queryFn: () => getGymPublic(),
  });

/** One feed's line in the diagnostics view. Internal: this names feeds, which `/public` never does. */
interface GymFeedProgress {
  feed: number;
  decisive: number;
  wins: number;
  rate: number | null;
  /** This feed's OWN measured null — the candidate's share of what it showed. Not a half. */
  null: number | null;
  /** Its e-value alone, against its own null: did THIS feed see anything? */
  e: number | null;
  shown_control: number;
  shown_candidate: number;
  starving: boolean;
}

interface GymProgress {
  status: string;
  decisive: number;
  wins: number;
  win_rate: number | null;
  low: number | null;
  high: number | null;
  e_value: number | null;
  null: number | null;
  reason: string | null;
  blocking: string[];
  needed: number | null;
  instrument: string | null;
  top_feed: number | null;
  top_feed_share: number | null;
  corroborated: boolean | null;
  starving_feeds: number[];
  /** Leaving the largest feed out, what do the others say? The comparison behind `corroborated`. */
  without_top_feed: {
    decisive: number;
    wins: number;
    rate: number | null;
    null: number | null;
    e: number | null;
  };
  feeds: GymFeedProgress[];
}

interface GymDiagnostics {
  /** `{feedId: displayName}` — the part that must never reach the public page. */
  feeds: Record<string, string>;
  tick: {
    watermark: string | null;
    ran_at: string | null;
    note: string | null;
    instrument: GymPublic["instrument"];
  } | null;
  experiments: {
    id: number;
    name: string;
    status: string;
    reason: string | null;
    proposedBy: string | null;
    opened: string | null;
    closed: string | null;
    starts: string | null;
    /** Frozen at the verdict once closed. */
    result: GymProgress | null;
    /** What the tick believes right now. Stale on a closed experiment, by definition. */
    progress: GymProgress | null;
  }[];
}

async function getGymDiagnostics(): Promise<GymDiagnostics> {
  // Credentials REQUIRED, unlike the public page: this route is admin-only and 404s otherwise.
  const response = await axios.get<GymDiagnostics>(
    `${host}/api/v1/gym/diagnostics`,
    requestSettings,
  );
  return response.data;
}

export const gymDiagnosticsQueryOptions = () =>
  queryOptions({
    queryKey: ["gym", "diagnostics"],
    queryFn: () => getGymDiagnostics(),
  });

export type { GymProgress };

export const gymFeedQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["gym", "feed", feedId],
    queryFn: () => getGymFeed(feedId),
  });
/* ------------------------------------------------------------------ *
 * Curation Desk
 *
 * Multi-curator curation for one feed. A desk names two feeds: the published
 * one readers see, and a "candidate" feed nobody serves whose looser rules
 * supply the queue. Curators promote from the second into the first.
 *
 * Permissions here are DESK MEMBERSHIP, not feed ownership -- the server
 * decides, and the UI only hides what the server would refuse anyway.
 * ------------------------------------------------------------------ */

type DeskRole = "reviewer" | "editor" | "owner";

type DeskCandidateState =
  | "pending"
  | "claimed"
  | "promoted"
  | "pinned"
  | "rejected"
  | "escalated";

export type Desk = {
  id: number;
  display_name: string;
  algorithm_id: number;
  candidate_algorithm_id: number | null;
  is_frozen: boolean;
  required_approvals: number;
  hourly_promotion_cap: number | null;
  role: DeskRole | null;
};

export type DeskCandidate = {
  id: number;
  post_uri: string;
  source: "candidate_feed" | "manual" | "curator_like" | "report";
  state: DeskCandidateState;
  approvals: number;
  reject_reason: string | null;
  claimed_by_user_id: number | null;
  claim_held: boolean;
  created_at: string | null;
};

type DeskQueue = {
  desk_id: number;
  your_role: DeskRole;
  is_frozen: boolean;
  candidates: DeskCandidate[];
};

/** What every act-on-a-candidate endpoint answers with. */
type DeskActionResult = {
  id: number;
  state: DeskCandidateState;
  expires_at?: string;
  claimed_until?: string;
};

type DeskEvent = {
  id: number;
  candidate_id: number | null;
  user_id: number | null;
  action: string;
  detail: Record<string, unknown> | null;
  at: string | null;
};

const curation = `${host}/api/v1/curation`;

async function getDesks(): Promise<{ desks: Desk[] }> {
  const response = await axios.get<{ desks: Desk[] }>(
    `${curation}/desks`,
    requestSettings,
  );
  return response.data;
}

export const desksQueryOptions = () =>
  queryOptions({ queryKey: ["desks"], queryFn: getDesks });

async function getDeskQueue(
  deskId: number,
  state: "pending" | "open" | "all" = "open",
): Promise<DeskQueue> {
  const response = await axios.get<DeskQueue>(
    `${curation}/desks/${deskId}/queue`,
    { ...requestSettings, params: { state } },
  );
  return response.data;
}

export const deskQueueQueryOptions = (
  deskId: number,
  state: "pending" | "open" | "all" = "open",
) =>
  queryOptions({
    queryKey: ["desk-queue", deskId, state],
    queryFn: () => getDeskQueue(deskId, state),
    // An election-night queue is shared by several people at once; a stale view means two
    // curators reaching for the same post.
    refetchInterval: 15_000,
  });

async function getDeskEvents(
  deskId: number,
): Promise<{ events: DeskEvent[] }> {
  const response = await axios.get<{ events: DeskEvent[] }>(
    `${curation}/desks/${deskId}/events`,
    requestSettings,
  );
  return response.data;
}

export const deskEventsQueryOptions = (deskId: number) =>
  queryOptions({
    queryKey: ["desk-events", deskId],
    queryFn: () => getDeskEvents(deskId),
  });

export async function createDesk(input: {
  display_name: string;
  algorithm_id: number;
  candidate_algorithm_id?: number | null;
  hourly_promotion_cap?: number | null;
}) {
  const response = await axios.post<{ id: number }>(
    `${curation}/desks`,
    input,
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["desks"] });
  return response.data;
}

export async function addDeskCandidate(deskId: number, postUrl: string) {
  const response = await axios.post<{ id: number; message: string }>(
    `${curation}/desks/${deskId}/candidates`,
    { post_url: postUrl },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["desk-queue", deskId] });
  return response.data;
}

export async function syncDeskCandidates(deskId: number) {
  const response = await axios.post<{ added: number; considered: number }>(
    `${curation}/desks/${deskId}/sync`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["desk-queue", deskId] });
  return response.data;
}

/** Take a candidate for a few minutes so two curators do not work the same post. */
export async function claimDeskCandidate(deskId: number, candidateId: number) {
  const response = await axios.post<DeskActionResult>(
    `${curation}/desks/${deskId}/candidates/${candidateId}/claim`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["desk-queue", deskId] });
  return response.data;
}

export async function promoteDeskCandidate(
  deskId: number,
  candidateId: number,
) {
  const response = await axios.post<DeskActionResult>(
    `${curation}/desks/${deskId}/candidates/${candidateId}/promote`,
    {},
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["desk-queue", deskId] }),
    queryClient.invalidateQueries({ queryKey: ["desk-events", deskId] }),
  ]);
  return response.data;
}

export async function pinDeskCandidate(
  deskId: number,
  candidateId: number,
  expiresInMinutes: number,
) {
  const response = await axios.post<DeskActionResult>(
    `${curation}/desks/${deskId}/candidates/${candidateId}/pin`,
    { expires_in_minutes: expiresInMinutes },
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["desk-queue", deskId] }),
    queryClient.invalidateQueries({ queryKey: ["desk-events", deskId] }),
  ]);
  return response.data;
}

export async function rejectDeskCandidate(
  deskId: number,
  candidateId: number,
  reason: string,
) {
  const response = await axios.post<DeskActionResult>(
    `${curation}/desks/${deskId}/candidates/${candidateId}/reject`,
    { reason },
    requestSettings,
  );
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["desk-queue", deskId] }),
    queryClient.invalidateQueries({ queryKey: ["desk-events", deskId] }),
  ]);
  return response.data;
}

/** The kill switch. Owner only; the server enforces that. */
export async function setDeskFrozen(deskId: number, frozen: boolean) {
  const response = await axios.post<{ id: number; is_frozen: boolean }>(
    `${curation}/desks/${deskId}/freeze`,
    {},
    { ...requestSettings, params: { frozen } },
  );
  await queryClient.invalidateQueries({ queryKey: ["desks"] });
  return response.data;
}

/* ------------------------------------------------------------------ *
 * Feed collaborators
 *
 * A feed used to have exactly one person who could change it. These let a
 * newsroom, a coalition or an election-night rota share one without sharing
 * the account.
 *
 * Permission is per-capability, and the server is the authority: the UI only
 * hides what the server would refuse anyway. `you_may_grant` comes back with
 * the collaborator list precisely so the grant screen can show a non-owner
 * only the boxes they are allowed to tick.
 * ------------------------------------------------------------------ */

export type FeedCapability =
  | "suggest_posts"
  | "add_posts_organic"
  | "add_posts_promoted"
  | "remove_posts"
  | "manage_sticky_posts"
  | "trigger_backfill"
  | "edit_feed_logic"
  | "apply_feed_logic"
  | "edit_feed_appearance"
  | "manage_collaborators";

export type CapabilityInfo = {
  key: FeedCapability;
  label: string;
  description: string;
};

export type FeedCollaborator = {
  id: number;
  user_id: number;
  did: string | null;
  /** Which bound list put them here. Null means a person added by hand. */
  source_list_id: number | null;
  capabilities: FeedCapability[];
  note: string | null;
  added_at: string | null;
};

type CollaboratorList = {
  owner_user_id: number;
  /**
   * The owner is not in `feed_collaborators` and never will be, so their identity comes
   * back separately. Without it the screen could not name who owns the feed, and a lone
   * collaborator row read as the owner.
   */
  owner_did: string | null;
  you_are_owner: boolean;
  your_capabilities: FeedCapability[];
  /** What YOU may hand to someone else. A non-owner can only pass on what they hold. */
  you_may_grant: FeedCapability[];
  collaborators: FeedCollaborator[];
};

type SharedFeed = {
  id: number;
  display_name: string;
  record_name: string;
  algorithm_uri: string;
  avatar_url: string | null;
  owner_user_id: number;
  capabilities: FeedCapability[];
  note: string | null;
  shared_at: string | null;
};

export type CollaboratorEvent = {
  id: number;
  actor_user_id: number | null;
  subject_user_id: number | null;
  /** Who did it, and who it was done to — the log stores ids, which name nobody. */
  actor_did: string | null;
  subject_did: string | null;
  action: string;
  /** Shape varies by action: `post_url`, `capabilities`, `hours`, `expires_at`, … */
  detail: Record<string, unknown> | null;
  at: string | null;
};

const collab = `${host}/api/v1/collab`;

/**
 * A post somebody has put forward, waiting on a decision.
 *
 * Stored as a curation-desk candidate rather than in a table of its own — the desk already
 * models exactly this, down to the unique (feed, post) constraint that makes "three people
 * wanted this" expressible. `approvals` counts distinct suggesters, which is why suggesting
 * something already suggested is an approval rather than a duplicate.
 */
type Suggestion = {
  id: number;
  post_uri: string;
  state: string;
  approvals: number;
  auto_accept_at: number | null;
  suggested_by: string[];
  at: string | null;
};

type SuggestionQueue = {
  suggestions: Suggestion[];
  auto_accept_at: number | null;
  you_may_suggest: boolean;
  you_may_accept: boolean;
};

async function getSuggestions(feedId: number) {
  const response = await axios.get<SuggestionQueue>(
    `${collab}/feeds/${feedId}/suggestions`,
    requestSettings,
  );
  return response.data;
}

export const suggestionsQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["feed-suggestions", feedId],
    queryFn: () => getSuggestions(feedId),
    retry: false,
  });

async function refreshSuggestions(feedId: number, inserted = false) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["feed-suggestions", feedId] }),
    queryClient.invalidateQueries({ queryKey: ["collaborator-events", feedId] }),
  ]);
  // Only when something actually reached the feed: the delayed double refetch is pointless
  // for a suggestion that is still sitting in the queue.
  if (inserted) {
    await settleFeedPreview(feedId);
  }
}

export async function suggestPost(feedId: number, postUrl: string) {
  const response = await axios.post<{
    message: string;
    approvals: number;
    auto_accepted: boolean;
    auto_accept_at: number | null;
  }>(`${collab}/feeds/${feedId}/suggestions`, { post_url: postUrl }, requestSettings);
  // A suggestion that hit the auto-accept threshold IS an insert.
  await refreshSuggestions(feedId, response.data.auto_accepted);
  return response.data;
}

export async function acceptSuggestion(input: {
  feedId: number;
  id: number;
  promoted?: boolean;
}) {
  const response = await axios.post<{ message: string }>(
    `${collab}/feeds/${input.feedId}/suggestions/${input.id}/accept`,
    {},
    { ...requestSettings, params: { promoted: !!input.promoted } },
  );
  await refreshSuggestions(input.feedId, true);
  return response.data;
}

export async function rejectSuggestion(feedId: number, id: number) {
  const response = await axios.post<{ message: string }>(
    `${collab}/feeds/${feedId}/suggestions/${id}/reject`,
    {},
    requestSettings,
  );
  await refreshSuggestions(feedId);
  return response.data;
}

export async function setAutoAcceptAt(feedId: number, at: number | null) {
  const response = await axios.patch<{ auto_accept_at: number | null }>(
    `${collab}/feeds/${feedId}/suggestions/settings`,
    { auto_accept_at: at },
    requestSettings,
  );
  await refreshSuggestions(feedId);
  return response.data;
}

/**
 * A public AT Protocol list bound to a feed.
 *
 * Being on it grants `capabilities` — but the grant is delivered as ordinary collaborator
 * rows written by a sync, not as a parallel kind of access. `last_sync_error` is the whole
 * reason this type carries sync state: a list that has started failing is why a roster
 * silently stops updating, and nothing else would say so.
 */
type BoundList = {
  id: number;
  list_url: string;
  display_name: string | null;
  capabilities: FeedCapability[];
  member_count: number | null;
  last_synced_at: string | null;
  last_sync_error: string | null;
};

async function getBoundLists(feedId: number) {
  const response = await axios.get<{ lists: BoundList[] }>(
    `${collab}/feeds/${feedId}/lists`,
    requestSettings,
  );
  return response.data.lists;
}

export const boundListsQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["collaborator-lists", feedId],
    queryFn: () => getBoundLists(feedId),
    retry: false,
  });

async function refreshLists(feedId: number) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["collaborator-lists", feedId] }),
    queryClient.invalidateQueries({ queryKey: ["collaborators", feedId] }),
    queryClient.invalidateQueries({ queryKey: ["collaborator-events", feedId] }),
  ]);
}

export async function bindList(input: {
  feedId: number;
  listUrl: string;
  capabilities: FeedCapability[];
}) {
  const response = await axios.post<{
    message: string;
    id: number;
    synced: boolean;
    added?: number;
    removed?: number;
    members?: number;
    error?: string;
  }>(
    `${collab}/feeds/${input.feedId}/lists`,
    { list_url: input.listUrl, capabilities: input.capabilities },
    requestSettings,
  );
  await refreshLists(input.feedId);
  return response.data;
}

export async function syncBoundList(feedId: number, listId: number) {
  const response = await axios.post<{
    synced: boolean;
    added?: number;
    removed?: number;
    members?: number;
    error?: string;
  }>(`${collab}/feeds/${feedId}/lists/${listId}/sync`, {}, requestSettings);
  await refreshLists(feedId);
  return response.data;
}

export async function unbindList(feedId: number, listId: number) {
  const response = await axios.delete<{ message: string; revoked: number }>(
    `${collab}/feeds/${feedId}/lists/${listId}`,
    requestSettings,
  );
  await refreshLists(feedId);
  return response.data;
}

async function getCapabilityCatalogue(): Promise<{
  capabilities: CapabilityInfo[];
  presets: Record<string, FeedCapability[]>;
}> {
  const response = await axios.get<{
    capabilities: CapabilityInfo[];
    presets: Record<string, FeedCapability[]>;
  }>(`${collab}/capabilities`, requestSettings);
  return response.data;
}

/**
 * A person you might add to a feed, as a human can recognise them.
 *
 * The grant screen used to take a typed handle, which is not a confirmable thing: two
 * accounts can differ by one character and the mistake only surfaces once the wrong person
 * can edit your feed. The picker searches and you choose a face.
 */
export type ActorCandidate = {
  did: string;
  handle: string | null;
  display_name: string | null;
  avatar: string | null;
};

async function searchActors(q: string): Promise<ActorCandidate[]> {
  const response = await axios.get<{ actors: ActorCandidate[] }>(
    `${collab}/search-actors`,
    { ...requestSettings, params: { q } },
  );
  return response.data.actors;
}

export const actorSearchQueryOptions = (q: string) =>
  queryOptions({
    queryKey: ["actor-search", q],
    queryFn: () => searchActors(q),
    enabled: q.trim().length > 0,
    // Keeping the previous page's results while the next search lands stops the list
    // flickering empty on every keystroke, which reads as "nobody found".
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    retry: false,
  });

/**
 * What happened to YOUR access lately, derived from the collaborator audit log.
 *
 * Same shape as the campaign notifications it sits beside. `link` is null for a removal:
 * they cannot open that feed any more, and a notification that 403s when pressed is worse
 * than one that only tells you.
 */
type CollaboratorNotification = {
  text: string;
  type: "info" | "success" | "warning";
  category:
    | "collaborator_added"
    | "capabilities_changed"
    | "collaborator_removed"
    // Your own suggestion was resolved by somebody else. Derived server-side from the
    // curation queue, not from the collaborator audit log like the three above.
    | "suggestion_accepted"
    | "suggestion_rejected";
  link: string | null;
  at: string | null;
  feed_id: number;
};

async function getCollaboratorNotifications() {
  const response = await axios.get<{
    notifications: CollaboratorNotification[];
  }>(`${collab}/notifications`, requestSettings);
  return response.data.notifications;
}

export const collaboratorNotificationsQueryOptions = () =>
  queryOptions({
    queryKey: ["collaborator-notifications"],
    queryFn: getCollaboratorNotifications,
    // Being removed from a feed should reach a tab that is already open, for the same
    // reason `shared-with-me` refetches: the event happens in somebody else's browser.
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

export const capabilityCatalogueQueryOptions = () =>
  queryOptions({
    queryKey: ["capability-catalogue"],
    queryFn: getCapabilityCatalogue,
    // The vocabulary changes when the server ships, not while someone is looking at it.
    staleTime: 60 * 60 * 1000,
  });

async function getSharedWithMe(): Promise<{ feeds: SharedFeed[] }> {
  const response = await axios.get<{ feeds: SharedFeed[] }>(
    `${collab}/shared-with-me`,
    requestSettings,
  );
  return response.data;
}

export const sharedWithMeQueryOptions = () =>
  queryOptions({
    queryKey: ["shared-with-me"],
    queryFn: getSharedWithMe,
    /**
     * 🔑 REVOCATION HAPPENS IN SOMEBODY ELSE'S BROWSER. Every other query here can rely on
     * the mutation that changed the data running in the same tab; this one cannot. The
     * owner revokes you from their session, and nothing tells yours — so a page left open
     * keeps showing the badge and the capability-gated controls for a feed you can no
     * longer touch, and every one of them 403s when pressed.
     *
     * Refetching when the tab is looked at again is the cheap half of the fix: coming back
     * to a tab is when a person is most likely to act on it. The honest half is that the
     * server refuses regardless, so the worst case is a stale button, never stale access.
     */
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 30 * 1000,
  });

async function getCollaborators(feedId: number): Promise<CollaboratorList> {
  const response = await axios.get<CollaboratorList>(
    `${collab}/feeds/${feedId}/collaborators`,
    requestSettings,
  );
  return response.data;
}

export const collaboratorsQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["collaborators", feedId],
    queryFn: () => getCollaborators(feedId),
  });

async function getCollaboratorEvents(
  feedId: number,
): Promise<{ events: CollaboratorEvent[] }> {
  const response = await axios.get<{ events: CollaboratorEvent[] }>(
    `${collab}/feeds/${feedId}/events`,
    requestSettings,
  );
  return response.data;
}

export const collaboratorEventsQueryOptions = (feedId: number) =>
  queryOptions({
    queryKey: ["collaborator-events", feedId],
    queryFn: () => getCollaboratorEvents(feedId),
  });

async function refreshCollaborators(feedId: number) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["collaborators", feedId] }),
    queryClient.invalidateQueries({ queryKey: ["collaborator-events", feedId] }),
  ]);
}

export async function addCollaborator(input: {
  feedId: number;
  /** A handle or a DID — a handle is what someone actually knows about a colleague. */
  actor: string;
  capabilities: FeedCapability[];
  note?: string;
}) {
  const response = await axios.post<{
    user_id: number;
    did: string;
    capabilities: FeedCapability[];
  }>(
    `${collab}/feeds/${input.feedId}/collaborators`,
    { actor: input.actor, capabilities: input.capabilities, note: input.note },
    requestSettings,
  );
  await refreshCollaborators(input.feedId);
  return response.data;
}

export async function updateCollaborator(input: {
  feedId: number;
  collaboratorId: number;
  capabilities: FeedCapability[];
  note?: string;
}) {
  const response = await axios.patch<{ capabilities: FeedCapability[] }>(
    `${collab}/feeds/${input.feedId}/collaborators/${input.collaboratorId}`,
    { capabilities: input.capabilities, note: input.note },
    requestSettings,
  );
  await refreshCollaborators(input.feedId);
  return response.data;
}

export async function removeCollaborator(feedId: number, collaboratorId: number) {
  const response = await axios.delete<{ message: string }>(
    `${collab}/feeds/${feedId}/collaborators/${collaboratorId}`,
    requestSettings,
  );
  await Promise.all([
    refreshCollaborators(feedId),
    queryClient.invalidateQueries({ queryKey: ["shared-with-me"] }),
  ]);
  return response.data;
}



/* ------------------------------------------------------------------ *
 * Collaborator actions
 *
 * The things a collaborator can DO once they hold a capability. Each one
 * is refused server-side without the matching grant, so these never need
 * to guess — the UI hides what it knows is unavailable, and the server is
 * still the authority if the two ever disagree.
 * ------------------------------------------------------------------ */

/**
 * Put a post into a feed you collaborate on.
 *
 * "organic" keeps the post's own timestamp, so it sits where the ordering puts it, exactly
 * as if the feed's rules had matched it — the post then ages down the feed normally.
 * "promoted" stamps it with the current time so it appears at the top. Two different amounts
 * of trust, hence two capabilities.
 */
export async function addCollaboratorPost(input: {
  feedId: number;
  postUrl: string;
  mode: "organic" | "promoted";
}) {
  const response = await axios.post<{ message: string }>(
    `${collab}/feeds/${input.feedId}/posts`,
    { post_url: input.postUrl, mode: input.mode },
    requestSettings,
  );
  await settleFeedPreview(input.feedId);
  return response.data;
}

/** Pin a post to the top of a feed you collaborate on. The expiry is mandatory. */
export async function pinCollaboratorPost(input: {
  feedId: number;
  postUrl: string;
  expiresInMinutes: number;
}) {
  const response = await axios.post<{ expires_at: string }>(
    `${collab}/feeds/${input.feedId}/sticky`,
    { post_url: input.postUrl, expires_in_minutes: input.expiresInMinutes },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["sticky-posts", input.feedId] });
  return response.data;
}

/**
 * Take a post out of a feed you collaborate on. `remove_posts`.
 *
 * The server hides it in `hidden_status`, which every serve query filters on, so it stays
 * out however often it re-matches. It also records who removed it, which is what
 * `restoreCollaboratorPost` reverses.
 *
 * 🔑 IT WANTS A DID-BASED at:// URI. The server checks the stored value against the feed's
 * rows literally, so a handle-based URI would be accepted and then hide nothing. Callers
 * holding a bsky.app link go through `resolvePostAtUri` first.
 */
export async function removeCollaboratorPost(input: {
  feedId: number;
  postUri: string;
  reason?: string;
}) {
  const response = await axios.delete<{ message: string; uri: string }>(
    `${collab}/feeds/${input.feedId}/posts`,
    {
      ...requestSettings,
      data: { post_url: input.postUri, reason: input.reason },
    },
  );
  await settleFeedPreview(input.feedId);
  return response.data;
}

/** Undo a collaborator removal. The post comes back the next time it matches. */
export async function restoreCollaboratorPost(input: {
  feedId: number;
  postUri: string;
}) {
  const response = await axios.post<{ message: string }>(
    `${collab}/feeds/${input.feedId}/posts/restore`,
    { post_url: input.postUri },
    requestSettings,
  );
  await settleFeedPreview(input.feedId);
  return response.data;
}

/**
 * The canonical at://did:… URI for whatever the person pasted.
 *
 * A bsky.app link names the author by HANDLE, and a feed's rows are keyed by DID, so the
 * naive rewrite produces a URI that matches nothing. Hydrating the post returns its real URI.
 */
export async function resolvePostAtUri(input: string): Promise<string> {
  const trimmed = input.trim();
  if (trimmed.startsWith("at://did:")) return trimmed;
  const post = await queryClient.fetchQuery(
    getHydratedPostQueryOptions(urlToAtUri(trimmed)),
  );
  return post.uri;
}

/** Take a pin down on a feed you collaborate on. `manage_sticky_posts`. */
export async function unpinCollaboratorPost(input: {
  feedId: number;
  stickyPostId: number;
  assocId?: number;
  stickyType?: string;
}) {
  const response = await axios.delete<{ message: string }>(
    `${collab}/feeds/${input.feedId}/sticky/${input.stickyPostId}`,
    {
      ...requestSettings,
      params: {
        sticky_type: input.stickyType ?? "pinned",
        ...(input.assocId ? { assoc_id: input.assocId } : {}),
      },
    },
  );
  await queryClient.invalidateQueries({
    queryKey: ["sticky-posts", input.feedId],
  });
  return response.data;
}

/**
 * Propose what the feed should match, as a DRAFT.
 *
 * 🔑 `edit_feed_logic` SAVES, IT DOES NOT PUBLISH — deliberately. Rules are the one thing
 * that changes the feed for every reader at once, so making a proposal live is its own
 * grant, `apply_feed_logic` (see `applyCollaboratorDraftLogic`). Without it the owner sees
 * the feed marked unpublished and reviews it.
 */
export async function saveCollaboratorDraftLogic(input: {
  feedId: number;
  manifest: Record<string, unknown>;
  order?: string;
  customSortSettings?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  emit?: string;
  onEmitNull?: string;
}) {
  const response = await axios.post<{ message: string }>(
    `${collab}/feeds/${input.feedId}/draft-logic`,
    {
      algorithm_manifest: input.manifest,
      order: input.order,
      custom_sort_settings: input.customSortSettings,
      metadata: input.metadata,
      emit: input.emit,
      on_emit_null: input.onEmitNull,
    },
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["feed", input.feedId] });
  return response.data;
}

/**
 * Change a feed's display name, description and avatar, for a collaborator holding
 * `edit_feed_appearance`.
 *
 * Takes the editor's own form and forwards only those fields: the slug (`record_name`) is
 * the feed's URL and stays the owner's, and the rules go through `draft-logic`. Until this
 * existed a collaborator's new avatar was silently dropped while the save said it worked.
 */
export async function saveCollaboratorAppearance(feedId: number, form: FormData) {
  const body = new FormData();
  for (const key of ["display_name", "description", "remove_avatar"]) {
    const value = form.get(key);
    if (typeof value === "string") body.set(key, value);
  }
  const file = form.get("file");
  if (file instanceof File && file.size > 0) body.set("file", file);
  const response = await axios.post<{ message: string; changed: string[] }>(
    `${collab}/feeds/${feedId}/appearance`,
    body,
    requestSetingsFormData,
  );
  if (response.data.changed.length > 0) {
    await queryClient.invalidateQueries({ queryKey: ["my-feeds"] });
    await queryClient.invalidateQueries({ queryKey: ["feed", feedId] });
    await queryClient.invalidateQueries({ queryKey: ["feed-preview", feedId] });
  }
  return response.data;
}

/**
 * Make the saved draft rules live, for a collaborator holding `apply_feed_logic`.
 *
 * The owner's `/publish_algo` is ownership-only and answers a collaborator with 403 ("not
 * yours to play with"), which is what every collaborator's Publish click got until this
 * existed. The server refuses a feed that has never been published — launching it stays
 * with the owner.
 */
export async function applyCollaboratorDraftLogic(feedId: number) {
  const response = await axios.post<{ message: string; applied: boolean }>(
    `${collab}/feeds/${feedId}/rules/apply`,
    {},
    requestSettings,
  );
  await queryClient.invalidateQueries({ queryKey: ["my-feeds", feedId] });
  await queryClient.invalidateQueries({ queryKey: ["feed", feedId] });
  return response.data;
}

/**
 * Re-scan recent history for posts that should have matched.
 *
 * The server refuses to stack a second job on a feed already mid-run and says so in
 * `reason`, so several collaborators reaching for this at once is harmless.
 */
export async function triggerCollaboratorBackfill(feedId: number, hours = 24) {
  const response = await axios.post<{ enqueued: boolean; reason: string }>(
    `${collab}/feeds/${feedId}/backfill`,
    {},
    { ...requestSettings, params: { hours } },
  );
  return response.data;
}

/**
 * atproto labelers, for the Labeler Label node.
 *
 * Shapes are the feed-processor endpoints in SPEC-atproto-labels-contract-2026-10.md
 * (`app/api/labelers.py`); `status` is membership-service's `LabelerStatus`, passed
 * through. Fetching a labeler's detail TRACKS it -- the server calls membership-service
 * `POST /v1/labelers/{did}/track` -- so opening a labeler on the canvas is what starts its
 * sync. That side effect is intended, and is why the detail is only fetched for a labeler
 * someone has actually chosen.
 */
async function searchLabelers(q: string, limit = 20): Promise<LabelerSummary[]> {
  const response = await axios.get<LabelerSummary[]>(
    `${host}/api/v1/labelers/search`,
    { ...requestSettings, params: { q, limit } },
  );
  return response.data;
}

export const searchLabelersQueryOptions = (q: string) =>
  queryOptions({
    queryKey: ["labeler-search", q.trim()],
    // An empty `q` is meaningful: the server answers with the most-liked labelers, which
    // is the useful list to open the picker on.
    queryFn: () => searchLabelers(q.trim()),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

async function getLabeler(didOrHandle: string): Promise<LabelerDetail> {
  const response = await axios.get<LabelerDetail>(
    `${host}/api/v1/labelers/${encodeURIComponent(didOrHandle)}`,
    requestSettings,
  );
  return response.data;
}

export const labelerQueryOptions = (didOrHandle: string) =>
  queryOptions({
    queryKey: ["labeler", didOrHandle],
    queryFn: () => getLabeler(didOrHandle),
    enabled: didOrHandle.trim().length > 0,
    retry: false,
    // While a labeler is not usable, the node shows why and Publish stays blocked, so
    // both need the status to move without a reload. See `labelerPollInterval`.
    refetchInterval: (query) =>
      labelerPollInterval(
        query.state.data?.status,
        query.state.status === "error",
      ),
  });
